import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import watermarkImage from '../assets/coverzone-watermark.png';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { useClientData } from '../hooks/useClientData';
import {  Vehicle, PersonOfInterest, DriverLicense, Property } from '../types';
import { useQuotes } from '../hooks/useQuotes';
import { CreateQuoteRequest } from '../types';
import AddPersonModal from '../components/AddPersonModal';
import AddVehicleModal, { EditableVehicleCard } from '../components/AddVehicleModal';
import axios from 'axios';
import AddHouseholdContentsModal, { EditableHouseholdContentsCard } from '../components/AddHouseholdContentsModal';
import TrailerCaravanModal, { EditableTrailerCaravanCard } from '../components/TrailerCaravanModal';
import QuoteValueAddedModal, { EditableValueAddedCard } from '../components/QuoteValueAdded';
import QuoteRiskItemsModal, { EditableRiskItemCard } from '../components/QuoteRiskItems';
import EditableNoteCard, { Note } from '../components/CreateQuoteEditableNoteCard';
import EditableFileCard, { UploadedFile } from '../components/CreateQuoteEditableFileCard';
import WaterCraftModal, { WaterCraft } from '../components/AddWaterCraftModal';
import EditableWaterCraftCard from '../components/CreateQuoteEditableWaterCraftCard';
import { EditablePropertyCard } from '../components/AddPropertyModal';
import { usePermissions } from '../context/PermissionsContext';

// Local extension to support the immediate-upload flow
type UploadedFileWithId = UploadedFile & {
  file_id?: string | number;
  alreadyUploaded?: boolean;
  document_type?: string;
  isTemp?: boolean;
};

const mapNoClaimsBonusForBackend = (frontendValue: string): string => {
  // Handle null/undefined/empty values
  if (!frontendValue || frontendValue === '') {
    return "None";
  }
  
  // Handle both "120 Months (10 Year)" and "120Months (10 Year)"
  let normalizedValue = frontendValue;
  try {
    normalizedValue = frontendValue.replace(/(\d+)\s*Months\s*\(\s*(\d+)\s*Year\s*\)/i, (match, months, years) => {
      return `${months} Months (${years} Year)`;
    });
  } catch (error) {
    console.warn('Error normalizing noClaimsBonus value:', frontendValue);
    normalizedValue = frontendValue;
  }

  const mapping: { [key: string]: string } = {
    "None": "None",
    "12 Months (1 Year)": "1 Year",
    "24 Months (2 Year)": "2 Year",
    "36 Months (3 Year)": "3 Year",
    "48 Months (4 Year)": "4 Year",
    "60 Months (5 Year)": "5 Year Plus",
    "72 Months (6 Year)": "5 Year Plus",
    "84 Months (7 Year)": "5 Year Plus",
    "96 Months (8 Year)": "5 Year Plus",
    "108 Months (9 Year)": "5 Year Plus",
    "120 Months (10 Year)": "5 Year Plus"
  };
  
  const result = mapping[normalizedValue] || "None";
  return result;
};

interface DeclarationQuestion {
  id: number;
  question: string;
  is_default: boolean;
}

interface CreateQuotePageProps {
  editingQuoteId?: string;
  quoteData?: any;
}

// claims interface
interface PreviousClaim {
  id?: string;
  description: string;
  policyNumber: string;
  previousInsurer: string;
  claimDate: string;
  claimValue: string;
  isEditing?: boolean;
}

interface PreviousInsurance {
  id?: string;
  description: string;
  policyNumber: string;
  insurer: string;
  startDate: string;
  endDate: string;
  isEditing?: boolean;
}

// type for consultants
interface Consultant {
  id: string;
  name: string;
}

const fallbackConsultantData: Consultant[] = [
  { id: '0', name: 'Default Consultant' },
];

const API_URL = process.env.REACT_APP_API_URL;

interface QuoteFormData {
  usage: string;
  // ... other form fields
}

const CreateQuotePage: React.FC<CreateQuotePageProps> = ({ editingQuoteId, quoteData }) => {
  const { can } = usePermissions();
  const location = useLocation();

  const [isEditing, setIsEditing] = useState(() => {
    const editMode = !!editingQuoteId || location.state?.isEditing;
    return editMode;
  });

  const [editingQuoteIdState, setEditingQuoteIdState] = useState<string | undefined>(editingQuoteId);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileWithId[]>([]);
  const [previousClaims, setPreviousClaims] = useState<PreviousClaim[]>([]);
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const { clientId } = useParams<{ clientId: string }>();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'warning' | 'info'>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  // ✅ Load initial state from sessionStorage to survive navigation
  const [quoteReference, setQuoteReference] = useState(() => sessionStorage.getItem(`draft_quote_ref_${clientId}`) || '');
  const { createQuote, loading, error } = useQuotes();
  const [isVehicleModalVisible, setIsVehicleModalVisible] = useState(false);
  const { user, client, isLoading } = useClientData(clientId);
  const [isWaterCraftModalVisible, setIsWaterCraftModalVisible] = useState(false);
  const [waterCrafts, setWaterCrafts] = useState<any[]>([]);
  const [declarationQuestions, setDeclarationQuestions] = useState<DeclarationQuestion[]>([]);
  const [selectedDeclarations, setSelectedDeclarations] = useState<{[key: number]: boolean}>(() => JSON.parse(sessionStorage.getItem(`draft_quote_declarations_${clientId}`) || '{}'));
  const [isLoadingDeclarations, setIsLoadingDeclarations] = useState(false);
  const [selectedAdministrators, setSelectedAdministrators] = useState<string[]>(() => JSON.parse(sessionStorage.getItem(`draft_quote_admin_${clientId}`) || '[]'));
  const [selectedUnderwriters, setSelectedUnderwriters] = useState<string[]>(() => JSON.parse(sessionStorage.getItem(`draft_quote_underwriter_${clientId}`) || '[]'));
  const [selectedDuration, setSelectedDuration] = useState(() => sessionStorage.getItem(`draft_quote_duration_${clientId}`) || '');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [formData, setFormData] = useState<QuoteFormData>({
    usage: '', // default value
    // ... other initial form values
  });
  const [isPersonModalVisible, setIsPersonModalVisible] = useState(false);
  const [personsOfInterest, setPersonsOfInterest] = useState<PersonOfInterest[]>([]);
  const [personFormData, setPersonFormData] = useState<Partial<PersonOfInterest>>({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: 'active',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: [],
  });
  
  const [personErrors, setPersonErrors] = useState({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  });

  const [administrators, setAdministrators] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingAdministrators, setIsLoadingAdministrators] = useState(false);
  const [underwriters, setUnderwriters] = useState<{ id: number; underwriter_name: string }[]>([]);

  const [isPreviousInsuranceModalOpen, setIsPreviousInsuranceModalOpen] = useState(false);

  // claims state variables
  const [previousClaim, setPreviousClaim] = useState<PreviousClaim>({
    description: '',
    policyNumber: '',
    previousInsurer: '',
    claimDate: '',
    claimValue: ''
  });

  const [previousInsurances, setPreviousInsurances] = useState<PreviousInsurance[]>([]);
  

  // Note modal state
  const [notes, setNotes] = useState<Note[]>([]);

  //consultants usestates
  const [assignToDifferentConsultant, setAssignToDifferentConsultant] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [consultants, setConsultants] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingConsultants, setIsLoadingConsultants] = useState(false);
  //Property states
  const [properties, setProperties] = useState<Property[]>([]);
  const [isHouseholdContentsModalVisible, setIsHouseholdContentsModalVisible] = useState(false);
  const [householdContents, setHouseholdContents] = useState<any[]>([]);

  const [isTrailerCaravanModalVisible, setIsTrailerCaravanModalVisible] = useState(false);
  const [trailersCaravans, setTrailersCaravans] = useState<any[]>([]);

  const [isValueAddedModalVisible, setIsValueAddedModalVisible] = useState(false);
  const [valueAddedProducts, setValueAddedProducts] = useState<any[]>([]);

  const [isRiskItemsModalVisible, setIsRiskItemsModalVisible] = useState(false);
  const [riskItems, setRiskItems] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [currentQuoteId, setCurrentQuoteId] = useState<number | null>(null);

  const [editingVehicleIndex, setEditingVehicleIndex] = useState<number | null>(null);
  const [editingVehicleData, setEditingVehicleData] = useState<Vehicle | null>(null);
  const hasLoadedInitialProducts = React.useRef(false);
  const isFirstLoad = React.useRef(true);
  const preventFreshResetRef = React.useRef(false);

    // ✅ CRITICAL: If we just came back from AddFilePage with a buffered file,
  // or any product page with refreshProducts, prevent the fresh reset from wiping it.
  if (
    !preventFreshResetRef.current &&
    (
      (window as any).__pendingQuoteFile ||
      location.state?.refreshProducts === true ||
      location.state?.uploadedFile
    )
  ) {
    preventFreshResetRef.current = true;
    console.log('🔒 Early-flag: preventFreshResetRef set to true (protecting drafts from fresh reset)');
  }
  
  // Define your list of insurance durations
  const insuranceDurations = [
    { id: '1', name: 'No Previous Insurance' },
    { id: '12', name: '12 Months (1 Year)' },
    { id: '24', name: '24 Months (2 Years)' },
    { id: '36', name: '36 Months (3 Years)' },
    { id: '48', name: '48 Months (4 Years)' },
    { id: '60', name: '60 Months (5 Years)' },
    { id: '72', name: '72 Months (6 Years)' },
    { id: '84', name: '84 Months (7 Years)' },
    { id: '96', name: '96 Months (8 Years)' },
    { id: '108', name: '108 Months (9 Years)' },
    { id: '120', name: '120 Months (10 Years)' },
    { id: '121', name: 'More than 120 Months (More than 10 Years)' },
  ];

  // List of products
  const products = [
    { id: '1', name: 'Property' },
    { id: '2', name: 'Household Contents' },
    { id: '3', name: 'Vehicle' },
    { id: '4', name: 'Trailer and Caravan' },
    { id: '5', name: 'All  Risk Items' },
    { id: '6', name: 'Value Added' },
    { id: '14', name: 'Water Craft' },
    { id: '7', name: 'Medical Aid' },
    { id: '8', name: 'GAB cover' },
    { id: '9', name: 'Risk' },
    { id: '10', name: 'Retirement' },
    { id: '11', name: 'Investment' },
    { id: '12', name: 'Will' },
    { id: '13', name: 'Other' },
  ];

    const usageOptions = [
      'Comprehensive',
      'Flood Cover Excluding',
      'Theft & Flood Cover Excluding',
      'Theft Cover Excluding',
      'Advensure',
      'Theft Cover Excluded'
    ];

    // Function to get card number for a specific product type
    const getCardNumber = (type: string, index: number, items: any[]) => {
      const itemsOfSameType = items.filter(item => {
        switch (type) {
          case 'property': return true; // All properties are same type
          case 'household': return true; // All household contents are same type
          case 'vehicle': return true; // All vehicles are same type
          case 'trailer': return true; // All trailers are same type
          case 'valueAdded': return true; // All value added are same type
          case 'riskItem': return true; // All risk items are same type
          case 'claim': return true; // All claims are same type
          case 'note': return true; // All notes are same type
          case 'file': return true; // All files are same type
          default: return true;
        }
      });
      
      const currentItem = items[index];
      const position = itemsOfSameType.findIndex(item => item === currentItem) + 1;
      return position;
    };

    // Function to get card title based on type
    const getCardTitle = (type: string) => {
      const titles: { [key: string]: string } = {
        property: 'Property',
        household: 'Household',
        vehicle: 'Vehicle',
        trailer: 'Trailer',
        valueAdded: 'Value Added',
        riskItem: 'Risk Item',
        claim: 'Claim',
        note: 'Note',
        file: 'File'
      };
      return titles[type] || 'Card';
    };

    // Helper function to format currency input
    const formatCurrencyInput = (value: string): string => {
      if (!value) return '';
      
      // Remove all non-digit characters except decimal point
      let cleaned = value.replace(/[^\d.]/g, '');
      
      // Remove multiple decimal points (keep only first one)
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // If it starts with a decimal, add 0 in front
      if (cleaned.startsWith('.')) {
        cleaned = '0' + cleaned;
      }
      
      // Split into whole and decimal parts
      const [wholePart, decimalPart = ''] = cleaned.split('.');
      
      // Format whole number part with commas
      const formattedWhole = wholePart ? 
        parseInt(wholePart, 10).toLocaleString('en-US') : 
        '0';
      
      // Handle decimal part (max 2 digits)
      const formattedDecimal = decimalPart.substring(0, 2);
      
      // Combine
      return formattedDecimal ? 
        `${formattedWhole}.${formattedDecimal}` : 
        formattedWhole;
    };

    // Helper function to log product addition (for activity logging)
const logProductAddition = async (quoteId: string | number | null, productType: string, productName: string, metadata: any = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    // If quoteId is null or undefined, we can't log yet
    if (!quoteId) {
      console.log('⏸️ Skipping activity log - no quote ID yet');
      return;
    }

    // Get user name from localStorage
    const userData = localStorage.getItem('user');
    let performedBy = 'System';
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const firstName = user.first_name || user.name || user.username || '';
        const lastName = user.last_name || user.surname || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        if (fullName) {
          performedBy = fullName;
        } else if (user.email) {
          performedBy = user.email;
        } else if (user.username) {
          performedBy = user.username;
        }
      } catch (parseError) {
        console.error('Error parsing user data:', parseError);
      }
    }

    await axios.post(
  `${API_URL}/api/quote-activity-logs`,
  {
    quote_id: typeof quoteId === 'string' ? parseInt(quoteId) : quoteId,
    action_type: 'product_added',
    description: `Product added: ${productName} (${getProductTypeDisplay(productType)})`,
    performed_by: performedBy,  // ✅ ADD THIS - Top level field
    metadata: {
      productType,
      productName,
      performedBy,
      ...metadata,
      addedAt: new Date().toISOString()
    }
  },
  { headers: { Authorization: `Bearer ${token}` } }
);
    console.log(`✅ Activity logged: product_added - ${productName} by ${performedBy}`);
  } catch (logError) {
    console.error('❌ Failed to log product addition:', logError);
    // Don't fail the navigation if logging fails
  }
};

// Helper function to get product type display name
const getProductTypeDisplay = (productType: string): string => {
  const typeMap: { [key: string]: string } = {
    'vehicle': 'Vehicle',
    'property': 'Property',
    'risk_item': 'Risk Item',
    'household': 'Household Contents',
    'trailer': 'Trailer & Caravan',
    'value_added': 'Value Added',
    'water_craft': 'Water Craft',
    'note': 'Note',
    'file': 'Document',
    'document': 'Document',
    'claim': 'Previous Claim',
    'previous_insurance': 'Previous Insurance'
  };
  
  return typeMap[productType] || productType || 'Product';
};

    // Helper function to format claim value for display
    const formatClaimValueForDisplay = (value: string): string => {
      if (!value) return '';
      
      // If it's already formatted with commas or contains letters, return as is
      if (value.includes(',') || value.includes('R') || /[a-zA-Z]/.test(value)) {
        return value;
      }
      
      // Try to parse as number
      const num = parseFloat(value);
      
      if (isNaN(num)) return value;
      
      // Format with commas and 2 decimal places
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    };

    // Helper function to parse claim value for storage
    const parseClaimValueForStorage = (displayValue: string): string => {
      if (!displayValue) return '';
      
      // Remove currency symbol and commas
      const cleaned = displayValue
        .replace(/[R\s,]/g, '')
        .replace(/[^0-9.]/g, '');
      
      return cleaned;
    };

    // function to handle saving from the vehicle modal
    const handleSaveVehicle = (vehicleData: Vehicle) => {

      const vehicleWithId = {
        ...vehicleData,
        client_id: parseInt(clientId!),
        id: editingVehicleIndex !== null
          ? vehicles[editingVehicleIndex]?.id
          : `temp-vehicle-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
      };

      // ────────────────────────────────────────────────
      // NEW: Prevent adding the exact same vehicle again
      // ────────────────────────────────────────────────
      const alreadyExists = vehicles.some(v =>
        v.registration_number === vehicleWithId.registration_number &&
        v.make === vehicleWithId.make &&
        v.model === vehicleWithId.model &&
        v.year === vehicleWithId.year
        
      );

      if (alreadyExists && editingVehicleIndex === null) {
        console.warn("Prevented duplicate vehicle add", {
          reg: vehicleWithId.registration_number,
          makeModel: `${vehicleWithId.make} ${vehicleWithId.model}`
        });
        setSaveStatus('warning');
        setSaveMessage("This vehicle appears to already be added!");
        setIsVehicleModalVisible(false);
        return;
      }

      if (editingVehicleIndex !== null) {
        handleSaveEdit('vehicle', editingVehicleIndex, vehicleWithId);
      } else {
        setVehicles(prev => {
          const newList = [...prev, vehicleWithId];
          return newList;
        });
      }

      setEditingVehicleIndex(null);
      setEditingVehicleData(null);
      setIsVehicleModalVisible(false);
      setSaveStatus('success');
      setSaveMessage(editingVehicleIndex !== null ? 'Vehicle updated!' : 'Vehicle added — saved on quote creation');
    };

    // Generic edit handler for any product type
    const handleEditItem = (type: string, index: number) => {
      switch (type) {
        case 'property':
          setProperties(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;
        case 'household':
          setHouseholdContents(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;
        case 'vehicle':
          // open the modal with the vehicle data
          setEditingVehicleIndex(index);
          setEditingVehicleData(vehicles[index]);
          setIsVehicleModalVisible(true);
          break;
        case 'trailer':
          setTrailersCaravans(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;
        case 'valueAdded':
          const productToEdit = valueAddedProducts[index];
          setValueAddedProducts(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;
        case 'riskItem':
          setRiskItems(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;
        case 'claim':
          setPreviousClaims(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;
        case 'note':
          setNotes(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;
        case 'file':
          setUploadedFiles(prev => prev.map((item, i) => 
            i === index ? { ...item, isEditing: true } : item
          ));
          break;

          case 'waterCraft':
            setWaterCrafts(prev => prev.map((item, i) => 
              i === index ? { ...item, isEditing: true } : item
            ));
            break;

      }
    };

// Generic save handler for any product type
const handleSaveEdit = async (type: string, index: number, updatedData: any) => {
  switch (type) {
    case 'property':
      setProperties(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;
    case 'household':
      setHouseholdContents(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;

    case 'vehicle':
      // Update the vehicle in state
      setVehicles(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;

    case 'trailer':
      setTrailersCaravans(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;
    case 'valueAdded':
      setValueAddedProducts(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      setSaveStatus('success');
      setSaveMessage('Value Added product updated — will be saved when quote is saved');
      break;
    case 'riskItem':
      setRiskItems(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;
    case 'claim':
      setPreviousClaims(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;
    case 'note':
      setNotes(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;
    case 'file':
      setUploadedFiles(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;

      case 'waterCraft':
      setWaterCrafts(prev => prev.map((item, i) => 
        i === index ? { ...updatedData, isEditing: false } : item
      ));
      break;
  }
};

// Generic cancel edit handler
const handleCancelEdit = (type: string, index: number) => {
  switch (type) {
    case 'property':
      setProperties(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'household':
      setHouseholdContents(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'vehicle':
      setVehicles(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'trailer':
      setTrailersCaravans(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'valueAdded':
      setValueAddedProducts(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'riskItem':
      setRiskItems(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'claim':
      setPreviousClaims(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'note':
      setNotes(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
    case 'file':
      setUploadedFiles(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;

    case 'waterCraft':
      setWaterCrafts(prev => prev.map((item, i) => 
        i === index ? { ...item, isEditing: false } : item
      ));
      break;
  }
};

    // handler for removing administrators
    const handleRemoveAdministrator = (adminId: string) => {
      setSelectedAdministrators(prev => {
        const newList = prev.filter(id => id !== adminId);
        sessionStorage.setItem(`draft_quote_admin_${clientId}`, JSON.stringify(newList));
        return newList;
      });
    };

    // handler for adding administrators from dropdown
    const handleAddAdministrator = (adminId: string) => {
      console.log('➕ handleAddAdministrator CALLED with:', adminId);
      
      if (adminId && !selectedAdministrators.includes(adminId)) {
        setSelectedAdministrators(prev => {
          const newList = [...prev, adminId];
          console.log('✅ New selectedAdministrators after set:', newList);
          // ✅ Save immediately to sessionStorage
          sessionStorage.setItem(`draft_quote_admin_${clientId}`, JSON.stringify(newList));
          return newList;
        });
      } else {
        console.log('⚠️ Admin already selected or invalid:', adminId);
      }
    };

    // handler for removing underwriters
    const handleRemoveUnderwriter = (underwriterId: string) => {
      setSelectedUnderwriters(prev => {
        const newList = prev.filter(id => id !== underwriterId);
        sessionStorage.setItem(`draft_quote_underwriter_${clientId}`, JSON.stringify(newList));
        return newList;
      });
    };

    // handler for adding underwriters from dropdown
    const handleAddUnderwriter = (underwriterId: string) => {
      if (underwriterId && !selectedUnderwriters.includes(underwriterId)) {
        setSelectedUnderwriters(prev => {
          const newList = [...prev, underwriterId];
          sessionStorage.setItem(`draft_quote_underwriter_${clientId}`, JSON.stringify(newList));
          return newList;
        });
      }
    };

    const handlePersonInputChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      const { name, value } = e.target;
      setPersonFormData(prev => ({ ...prev, [name]: value }));
      setPersonErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handlePersonSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      const newErrors = {
        name: !personFormData.name ? 'Name is required' : '',
        surname: !personFormData.surname ? 'Surname is required' : '',
        email: !personFormData.email ? 'Email is required' : '',
        designation: !personFormData.designation ? 'Designation is required' : '',
        status: !personFormData.status ? 'Status is required' : '',
        contact_number: '',
        id_passport_number: '',
        date_of_birth: '',
        drivers_license_codes: '',
        server: '',
      };

      setPersonErrors(newErrors);

      // Check if there are any validation errors
      const hasErrors = Object.values(newErrors).some(error => error !== '');
      
      if (hasErrors) {
        return;
      }

      if (Object.values(newErrors).some(error => error)) {
        return;
      }
      if (!clientId || isNaN(parseInt(clientId))) {
        setPersonErrors((prev) => ({ ...prev, server: 'Valid clientId is required' }));
        return;
      }      

      try {        

        const savedPerson = await savePersonToDatabase(personFormData, clientId);        

        setPersonsOfInterest([...personsOfInterest, savedPerson]);
        setIsPersonModalVisible(false);
        setSaveStatus('success');
        setSaveMessage('Person added successfully!');
      } catch (error) {
        console.error('❌ Error in handlePersonSubmit:', error);
        if (error instanceof Error) {
          setSaveStatus('error');
          setSaveMessage(`Failed to add person: ${error.message}`);
          console.error('Error saving person:', error);
        } else {
          setSaveStatus('error');
          setSaveMessage('Failed to add person: Unknown error');
          console.error('Error saving person:', error);
        }
      }
    };

    const handleClosePersonModal = () => {
    setIsPersonModalVisible(false);
    setPersonFormData({
      name: '',
      surname: '',
      email: '',
      designation: '',
      status: '',
      contact_number: '',
      id_passport_number: '',
      date_of_birth: '',
      drivers_license_codes: [],
    });
    setPersonErrors({
      name: '',
      surname: '',
      email: '',
      designation: '',
      status: '',
      contact_number: '',
      id_passport_number: '',
      date_of_birth: '',
      drivers_license_codes: '',
      server: '',
    });
  };

  const handleFormDataChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

    const savePersonToDatabase = async (personData: Partial<PersonOfInterest>, clientId: string) => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }
        if (!clientId || isNaN(parseInt(clientId))) {
          throw new Error('Valid clientId is required');
        }
        
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/persons-of-interest`;        
        
        // Send clientId as a string (backend will parse it)
        const requestData = {
          clientId: clientId,
          ...personData,
          drivers_license_codes: personData.drivers_license_codes || []  // Send as array, not string
        };            
        
        const response = await axios.post(
          apiUrl,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            timeout: 10000,
          }
        );
                
        return response.data;
        
      } catch (error) {        
        
        if (axios.isAxiosError(error)) {          
          
          if (error.response) {            
            
            throw new Error(`Server error (${error.response.status}): ${error.response.data.message || 'Unknown error'}`);
          } else if (error.request) {            
            throw new Error('No response from server. Please check if the backend server is running.');
          } else {
            throw new Error(`Request setup error: ${error.message}`);
          }
        }
        throw error;
      }
    };

    // ✅ ADD THIS FUNCTION HERE - Upload file immediately when selected
  const uploadFileImmediately = async (file: File, description: string, clientId: string): Promise<{ success: boolean; fileId?: string; fileData?: any; error?: string }> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const formData = new FormData();
      formData.append('files', file);
      formData.append('clientId', clientId);
      formData.append('description', description);

      console.log(`📤 Uploading file immediately: ${file.name} (${file.size} bytes)`);

      const response = await axios.post(
        `${API_URL}/api/quotes/upload-temp-file`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      console.log(`✅ File uploaded immediately:`, response.data);
      
      return {
        success: true,
        fileId: response.data.fileId || response.data.id,
        fileData: response.data
      };
    } catch (error: any) {
      console.error(`❌ Failed to upload file immediately:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Upload failed'
      };
    }
  };

    useEffect(() => {
      const fetchPersonsOfInterest = async () => {
        if (!clientId) return;
        
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/persons-of-interest?clientId=${clientId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('🔍 RAW persons data from API:', data);
            
            // Parse driver licenses for each person
            const mappedPersons = data.map((person: any) => {
              let licenses: DriverLicense[] = [];
              
              // Check multiple possible field names
              const licenseData = person.drivers_license_codes || person.drivers_license_code;
              
              console.log(`🔍 Processing ${person.name} ${person.surname}:`, {
                licenseData,
                type: typeof licenseData,
                isArray: Array.isArray(licenseData)
              });
              
              if (licenseData) {
                // If it's already an array of objects (from backend fix)
                if (Array.isArray(licenseData)) {
                  licenses = licenseData.map((l: any) => ({
                    code: l.code || '',
                    dateOfFirstIssue: l.dateOfFirstIssue || l.date_of_first_issue || '',
                    expiresOn: l.expiresOn || l.expires_on || ''
                  }));
                }
                // If it's a string (pipe-delimited format from database)
                else if (typeof licenseData === 'string' && licenseData.includes('|')) {
                  // Format: "CODE|ISSUE_DATE|EXPIRY_DATE"
                  const parts = licenseData.split('|');
                  licenses = [{
                    code: parts[0]?.trim() || '',
                    dateOfFirstIssue: parts[1]?.trim() || '',
                    expiresOn: parts[2]?.trim() || ''
                  }];
                }
                // If it's a JSON string
                else if (typeof licenseData === 'string' && (licenseData.startsWith('[') || licenseData.startsWith('{'))) {
                  try {
                    const parsed = JSON.parse(licenseData);
                    if (Array.isArray(parsed)) {
                      licenses = parsed.map((l: any) => ({
                        code: l.code || '',
                        dateOfFirstIssue: l.dateOfFirstIssue || l.date_of_first_issue || '',
                        expiresOn: l.expiresOn || l.expires_on || ''
                      }));
                    } else {
                      licenses = [{
                        code: parsed.code || '',
                        dateOfFirstIssue: parsed.dateOfFirstIssue || parsed.date_of_first_issue || '',
                        expiresOn: parsed.expiresOn || parsed.expires_on || ''
                      }];
                    }
                  } catch (e) {
                    // Not valid JSON, treat as plain string
                    if (licenseData.trim()) {
                      licenses = [{ code: licenseData.trim(), dateOfFirstIssue: '', expiresOn: '' }];
                    }
                  }
                }
                // If it's a plain string (single code)
                else if (typeof licenseData === 'string' && licenseData.trim()) {
                  licenses = [{ code: licenseData.trim(), dateOfFirstIssue: '', expiresOn: '' }];
                }
              }
              
              console.log(`✅ Parsed licenses for ${person.name}:`, licenses);
              
              return {
                ...person,
                drivers_license_codes: licenses
              };
            });
            
            console.log('✅ Mapped persons with licenses:', mappedPersons.map((p: any) => ({
              name: p.name,
              licenses: p.drivers_license_codes
            })));
            
            setPersonsOfInterest(mappedPersons);
          }
        } catch (error) {
          console.error('Error fetching persons of interest:', error);
        }
      };
      
      fetchPersonsOfInterest();
    }, [clientId]);

// Listen for refreshProducts from navigation state
useEffect(() => {
  const shouldRefresh = location.state?.refreshProducts;
  
  if (shouldRefresh && clientId) {
    console.log('🔄 Refreshing products after returning from add page');
    
    // ✅ Set the ref flag to prevent the fresh quote reset
    preventFreshResetRef.current = true;
    console.log('🔒 preventFreshResetRef set to true');
    
    // Load all drafts but MERGE with existing
    if (previousInsurances.length === 0) {
      loadDraftPreviousInsurances();
    } else {
      // Just add new ones without clearing existing
      const storageKey = `client_${clientId}_previous_insurances_draft`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const draftInsurances = JSON.parse(stored);
          const formattedInsurances = draftInsurances.map((ins: any) => ({
            id: ins.id || `ins-${Date.now()}-${Math.random()}`,
            description: ins.description || '',
            policyNumber: ins.policy_number || ins.policyNumber || '',
            insurer: ins.insurer || '',
            startDate: ins.start_date || ins.startDate || '',
            endDate: ins.end_date || ins.endDate || '',
            isEditing: false,
            isTemp: true
          }));
          
          setPreviousInsurances(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const newInsurances = formattedInsurances.filter((ins: any) => !existingIds.has(ins.id));
            if (newInsurances.length === 0) return prev;
            console.log(`Adding ${newInsurances.length} new insurances from draft`);
            return [...prev, ...newInsurances];
          });
        } catch (error) {
          console.error('Error loading draft insurances:', error);
        }
      }
    }
    
    // Similar for claims
    if (previousClaims.length === 0) {
      loadDraftPreviousClaims();
    } else {
      const storageKey = `client_${clientId}_previous_claims_draft`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const draftClaims = JSON.parse(stored);
          const formattedClaims = draftClaims.map((claim: any) => ({
            id: claim.id || `claim-${Date.now()}-${Math.random()}`,
            description: claim.description || '',
            policyNumber: claim.policy_number || claim.policyNumber || '',
            previousInsurer: claim.previous_insurer || claim.previousInsurer || '',
            claimDate: claim.claim_date || claim.claimDate || '',
            claimValue: claim.claim_value?.toString() || claim.claimValue?.toString() || '',
            isEditing: false,
            isTemp: true
          }));
          
          setPreviousClaims(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const newClaims = formattedClaims.filter((claim: any) => !existingIds.has(claim.id));
            if (newClaims.length === 0) return prev;
            console.log(`Adding ${newClaims.length} new claims from draft`);
            return [...prev, ...newClaims];
          });
        } catch (error) {
          console.error('Error loading draft claims:', error);
        }
      }
    }
    
    // ✅ Load risk items from draft
    const riskStorageKey = `client_${clientId}_risk_items_draft`;
    const riskStored = localStorage.getItem(riskStorageKey);
    if (riskStored) {
      try {
        const draftItems = JSON.parse(riskStored);
        console.log('📦 Raw draft risk items:', draftItems);
        
        const formattedItems = draftItems.map((item: any) => ({
          id: item.id || `temp-riskitem-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
          itemType: item.item_type || item.itemType || '',
          itemName: item.item_name || item.itemName || '',
          description: item.description || '',
          serialNumber: item.serial_number || item.serialNumber || '',
          coverType: item.cover_type || item.coverType || 'Comprehensive',
          insuredValue: item.insured_value || item.insuredValue || 0,
          premium: item.premium || 0,
          excess: item.excess || 0,
          noClaimsBonus: item.no_claims_bonus || item.noClaimsBonus || 'None',
          isEditing: false,
          isTemp: true
        }));
        
        setRiskItems(prev => {
          const existingIds = new Set(prev.map(r => r.id));
          const newItems = formattedItems.filter((r: any) => !existingIds.has(r.id));
          if (newItems.length === 0) return prev;
          console.log(`Adding ${newItems.length} new draft risk items`);
          return [...prev, ...newItems];
        });
      } catch (error) {
        console.error('Error loading draft risk items:', error);
      }
    }
    
    // Clear the refresh flag but preserve state
    navigate(location.pathname, { 
      replace: true,
      state: { ...location.state, refreshProducts: false }
    });
    
    // ✅ Reset the flag after a delay
    setTimeout(() => {
      preventFreshResetRef.current = false;
      console.log('🔓 preventFreshResetRef reset to false');
    }, 2000);
  }
}, [location.state?.refreshProducts, clientId, navigate, location.pathname, previousInsurances.length, previousClaims.length]);

// Listen for file returns from add-file page
useEffect(() => {
  const locationState = location.state as any;
  if (locationState?.uploadedFile && clientId) {
    const { fileId, fileName, fileSize, fileType, description } = locationState.uploadedFile;
    
    console.log('📎 Received uploaded file from add-file page:', { fileId, fileName });
    
    // Store the file metadata with the ID from immediate upload
    const newFile: any = {
      id: fileId,
      file_name: fileName,
      file_size: fileSize,
      file_type: fileType,
      description: description,
      isEditing: false,
      isTemp: false,
      file_id: fileId,
      alreadyUploaded: true
    };
    
    setUploadedFiles(prev => [...prev, newFile]);
    
    setSaveStatus('success');
    setSaveMessage(`File "${fileName}" uploaded successfully!`);
    
    setTimeout(() => {
      setSaveStatus('idle');
      setSaveMessage('');
    }, 3000);
    
    // Clear the navigation state
    navigate(location.pathname, { replace: true, state: {} });
  }
}, [location, clientId, navigate]);

// ✅ Pick up a buffered file from AddFilePage (when quote didn't exist yet)
useEffect(() => {
  const pendingFile = (window as any).__pendingQuoteFile;
  if (!pendingFile) return;

  console.log('📎 Picked up buffered file from AddFilePage:', pendingFile.file.name);
  
  // ✅ Don't clear immediately — keep the file around briefly so StrictMode double-mount works
  const pendingBackup = pendingFile;
  setTimeout(() => {
    if ((window as any).__pendingQuoteFile === pendingBackup) {
      (window as any).__pendingQuoteFile = null;
    }
  }, 500);

  // ✅ Create a temporary object URL so the card can render a preview
  const previewUrl = URL.createObjectURL(pendingFile.file);

  setUploadedFiles(prev => {
    // ✅ Idempotent: don't add the same file twice
    const alreadyAdded = prev.some(f => 
      f.file_name === pendingFile.file.name && 
      (f as any)._fileObject === pendingFile.file
    );
    if (alreadyAdded) {
      console.log('⏸️ File already in uploadedFiles state, skipping duplicate add');
      return prev;
    }
    
    return [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        file_name: pendingFile.file.name,
        file_size: pendingFile.file.size,
        file_type: pendingFile.file.type,
        document_type: pendingFile.document_type,
        description: pendingFile.description,
        isEditing: false,
        isTemp: true,
        url: previewUrl,
        _fileObject: pendingFile.file,
      } as any,
    ];
  });
}, []);

    // Add this useEffect after your state declarations (around line 220)
    useEffect(() => {
      // Clean up duplicate value added products
      if (valueAddedProducts.length > 0) {
        const uniqueProducts = valueAddedProducts.reduce((acc: any[], current) => {
          // Check if current product already exists in accumulator
          const exists = acc.some(item => 
            item.id === current.id || 
            (item.productType === current.productType && 
            item.description === current.description &&
            item.value === current.value)
          );
          
          if (!exists) {
            acc.push(current);
          } else {
            console.warn('🧹 Removing duplicate value added product:', current);
          }
          
          return acc;
        }, []);
        
        // Only update state if duplicates were removed
        if (uniqueProducts.length !== valueAddedProducts.length) {
          console.log(`🧹 Removed ${valueAddedProducts.length - uniqueProducts.length} duplicate(s)`);
          setValueAddedProducts(uniqueProducts);
        }
      }
    }, [valueAddedProducts]);

      useEffect(() => {
    fetchDeclarationQuestions();
  }, []);

  // ✅ Persist form state to sessionStorage whenever it changes
  useEffect(() => {
    if (!clientId) return;
    sessionStorage.setItem(`draft_quote_ref_${clientId}`, quoteReference);
    sessionStorage.setItem(`draft_quote_duration_${clientId}`, selectedDuration);
    sessionStorage.setItem(`draft_quote_admin_${clientId}`, JSON.stringify(selectedAdministrators));
    sessionStorage.setItem(`draft_quote_underwriter_${clientId}`, JSON.stringify(selectedUnderwriters));
    sessionStorage.setItem(`draft_quote_declarations_${clientId}`, JSON.stringify(selectedDeclarations));
  }, [quoteReference, selectedDuration, selectedAdministrators, selectedUnderwriters, selectedDeclarations, clientId]);

    const fetchDeclarationQuestions = async () => {
  setIsLoadingDeclarations(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    const response = await fetch(`${API_URL}/api/quotation/declaration-questions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      // Direct array response (not wrapped in {success, data})
      const questions = await response.json();
      
      setDeclarationQuestions(questions);
      
      // ✅ FIX: Only apply defaults if there is NO saved draft in sessionStorage
      const savedDraft = sessionStorage.getItem(`draft_quote_declarations_${clientId}`);
      
      if (!savedDraft) {
        // Initialize selected declarations with default questions checked
        const initialSelected: {[key: number]: boolean} = {};
        questions.forEach((question: DeclarationQuestion) => {
          initialSelected[question.id] = question.is_default;
        });
        setSelectedDeclarations(initialSelected);
      } else {
        console.log('🛡️ Preserving saved declaration state from sessionStorage');
      }
    }
  } catch (error) {
    console.error('Error fetching declaration questions:', error);
    // Use fallback questions
    const fallbackQuestions = [
      { id: 1, question: 'Do you confirm that all information provided is accurate and complete?', is_default: true },
      { id: 2, question: 'Have you disclosed all material facts relevant to this quotation?', is_default: false },
      { id: 3, question: 'Do you authorize us to verify the information provided?', is_default: false },
    ];
    setDeclarationQuestions(fallbackQuestions);
    
    const initialSelected: {[key: number]: boolean} = {};
    fallbackQuestions.forEach((question: DeclarationQuestion) => {
      initialSelected[question.id] = question.is_default;
    });
    setSelectedDeclarations(initialSelected);
  } finally {
    setIsLoadingDeclarations(false);
  }
};

  useEffect(() => {
    if (saveStatus !== 'idle') {
      const timer = setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);

  useEffect(() => {
    const fetchAdministrators = async () => {
      // Check if we have a saved draft in sessionStorage
      const savedDraftAdmins = sessionStorage.getItem(`draft_quote_admin_${clientId}`);
      if (savedDraftAdmins) {
        const parsed = JSON.parse(savedDraftAdmins);
        if (parsed.length > 0) {
          console.log('🛡️ Admin component mounted - already have saved admins:', parsed);
          setSelectedAdministrators(parsed);
        }
      }

      setIsLoadingAdministrators(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/administrators`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        // Handle different response structures
        const adminsData = response.data.data || response.data || [];
        
        // Transform the data to match the expected format
        const formattedAdmins = adminsData.map((admin: any) => ({
          id: admin.id?.toString() || admin.administrator_id?.toString(),
          name: admin.name || `${admin.first_name || ''} ${admin.last_name || ''}`.trim()
        })).filter((admin: any) => admin.id && admin.name);
        
        setAdministrators(formattedAdmins);
        
      } catch (error) {
        // Fallback to empty array if fetch fails
        setAdministrators([]);
      } finally {
        setIsLoadingAdministrators(false);
      }
    };

    fetchAdministrators();
  }, []);

  // Restore administrators when returning from add product page
useEffect(() => {
  // ✅ Check sessionStorage first (our new state persistence)
  const sessionAdmins = sessionStorage.getItem(`draft_quote_admin_${clientId}`);
  const sessionUnderwriters = sessionStorage.getItem(`draft_quote_underwriter_${clientId}`);
  
  if (sessionAdmins) {
    const parsedAdmins = JSON.parse(sessionAdmins);
    if (parsedAdmins.length > 0) {
      console.log('🛡️ Restoring administrators from sessionStorage:', parsedAdmins);
      setSelectedAdministrators(parsedAdmins);
      return; // Stop here, we already have the correct state
    }
  }

  // Fallback to localStorage if sessionStorage is empty
  const savedAdmins = localStorage.getItem('temp_selected_administrators');
  const savedUnderwriters = localStorage.getItem('temp_selected_underwriters');
  
  if (savedAdmins) {
    const parsedAdmins = JSON.parse(savedAdmins);
    if (parsedAdmins.length > 0) {
      console.log('🔄 Restoring administrators from localStorage:', parsedAdmins);
      setSelectedAdministrators(parsedAdmins);
    }
  }
  
  if (savedUnderwriters) {
    const parsedUnderwriters = JSON.parse(savedUnderwriters);
    if (parsedUnderwriters.length > 0) {
      console.log('🔄 Restoring underwriters from localStorage:', parsedUnderwriters);
      setSelectedUnderwriters(parsedUnderwriters);
    }
  }
}, [clientId]);

    // fetch underwriters  
    useEffect(() => {
      const fetchUnderwriters = async () => {
        try {
          const token = localStorage.getItem('token');
          
          // Add debug log
          console.log('🔑 Token check for underwriters:', {
            hasToken: !!token,
            tokenLength: token?.length,
            tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token'
          });
          
          if (!token) {
            console.error('❌ No token found for fetching underwriters');
            // Redirect to login or show error
            return;
          }
          
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/underwriters`,
            {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('✅ Underwriters fetched successfully:', response.data);
          
          // 🔥 CRITICAL: Set the fetched data to state
          const underwritersData = response.data.data || response.data || [];
          setUnderwriters(underwritersData);
          
          return underwritersData;
        } catch (error: any) {
          console.error('❌ Error fetching underwriters:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.response?.data?.message,
            headers: error.config?.headers
          });
          
          // Handle 401 specifically
          if (error.response?.status === 401) {
            console.error('🔐 Authentication failed. Token might be expired or invalid.');
            // Clear token and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
          
          // Set empty array on error
          setUnderwriters([]);
          return [];
        }
      };

      fetchUnderwriters();
    }, []);

  //useEffect to fetch consultants
  useEffect(() => {
    const fetchConsultants = async () => {
      setIsLoadingConsultants(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');
        const url = `${API_URL}/api/consultants`;
        //console.log('🔍 Fetching consultants from:', url);
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        //console.log('📊 Response status:', response.status, response.statusText);
        if (!response.ok) {
          let errorMessage = `Server error: ${response.status} ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            const errorText = await response.text();
            if (errorText) errorMessage = errorText;
          }
          throw new Error(errorMessage);
        }
        const data = await response.json();
        //console.log('✅ Consultants data received:', data);
        // Adjust based on your API response structure
        const formattedConsultants = (data.data || data || []).map((consultant: any) => {
          // ✅ Combine first_name and last_name if name is not provided
          const firstName = consultant.first_name || consultant.name || '';
          const lastName = consultant.last_name || consultant.surname || '';
          const displayName = `${firstName} ${lastName}`.trim() || `Consultant ${consultant.id}`;
          
          return {
            id: consultant.id?.toString(),
            name: displayName
          };
        }).filter((consultant: any) => consultant.id && consultant.name);
        setConsultants(formattedConsultants);
      } catch (error) {
        setSaveStatus('error');
        setSaveMessage(`Failed to load consultants: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setConsultants(fallbackConsultantData);
      } finally {
        setIsLoadingConsultants(false);
      }
    };
    fetchConsultants();
  }, []);

const saveProductsToLocalStorage = (quoteId?: string) => {
  const productsData = {
    properties,
    vehicles,
    householdContents,
    trailersCaravans,
    valueAddedProducts,
    riskItems,
    waterCrafts,
    previousClaims,
    previousInsurances,
    notes,
    uploadedFiles,
    clientId,
    quoteId: quoteId || null,
    timestamp: new Date().toISOString()
  };
  
  // Always save to client draft storage
  localStorage.setItem(`client_${clientId}_products`, JSON.stringify(productsData));
  console.log('💾 Auto-saved draft products to localStorage');
  
  // Also save with quote ID if available
  if (quoteId) {
    localStorage.setItem(`quote_${quoteId}_products`, JSON.stringify(productsData));
    console.log(`💾 Saved products to localStorage with quote ID: ${quoteId}`);
  }
};

const loadProductsFromLocalStorage = () => {
  const stored = localStorage.getItem(`client_${clientId}_products`);
  if (stored) {
    try {
      const productsData = JSON.parse(stored);
      setProperties(productsData.properties || []);
      setVehicles(productsData.vehicles || []);
      setHouseholdContents(productsData.householdContents || []);
      setTrailersCaravans(productsData.trailersCaravans || []);
      setValueAddedProducts(productsData.valueAddedProducts || []);
      setRiskItems(productsData.riskItems || []);
      setWaterCrafts(productsData.waterCrafts || []);
      setPreviousClaims(productsData.previousClaims || []);
      setNotes(productsData.notes || []);
      setUploadedFiles(productsData.uploadedFiles || []);
      return true;
    } catch (error) {
      console.error('Error loading products from localStorage:', error);
      return false;
    }
  }
  return false;
};

// Load draft previous insurances from localStorage (for products added before quote creation)
const loadDraftPreviousInsurances = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_previous_insurances_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftInsurances = JSON.parse(stored);
      console.log(`📦 Loaded ${draftInsurances.length} draft previous insurances from localStorage`);
      
      const formattedInsurances = draftInsurances.map((ins: any) => ({
        id: ins.id || `ins-${Date.now()}-${Math.random()}`,
        description: ins.description || '',
        policyNumber: ins.policy_number || ins.policyNumber || '',
        insurer: ins.insurer || '',
        startDate: ins.start_date || ins.startDate || '',
        endDate: ins.end_date || ins.endDate || '',
        isEditing: false,
        isTemp: true
      }));
      
      setPreviousInsurances(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newInsurances = formattedInsurances.filter((ins: any) => !existingIds.has(ins.id));
        
        if (newInsurances.length === 0) {
          console.log('No new draft insurances to add');
          return prev;
        }
        
        console.log(`Adding ${newInsurances.length} new draft insurances`);
        return [...prev, ...newInsurances];  // Changed order to preserve existing
      });
      
      // Don't remove the draft immediately - keep it until quote is saved
      // localStorage.removeItem(storageKey);
      
    } catch (error) {
      console.error('Error loading draft previous insurances:', error);
    }
  }
};

// Load draft previous claims from localStorage (for products added before quote creation)
const loadDraftPreviousClaims = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_previous_claims_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftClaims = JSON.parse(stored);
      console.log(`📦 Loaded ${draftClaims.length} draft previous claims from localStorage`);
      
      const formattedClaims = draftClaims.map((claim: any) => ({
        id: claim.id || `claim-${Date.now()}-${Math.random()}`,
        description: claim.description || '',
        policyNumber: claim.policy_number || claim.policyNumber || '',
        previousInsurer: claim.previous_insurer || claim.previousInsurer || '',
        claimDate: claim.claim_date || claim.claimDate || '',
        claimValue: claim.claim_value?.toString() || claim.claimValue?.toString() || '',
        isEditing: false,
        isTemp: true
      }));
      
      setPreviousClaims(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newClaims = formattedClaims.filter((claim: any) => !existingIds.has(claim.id));
        
        if (newClaims.length === 0) {
          console.log('No new draft claims to add');
          return prev;
        }
        
        console.log(`Adding ${newClaims.length} new draft claims`);
        return [...prev, ...newClaims];  // Changed order to preserve existing
      });
      
      // Don't remove the draft immediately - keep it until quote is saved
      // localStorage.removeItem(storageKey);
      
    } catch (error) {
      console.error('Error loading draft previous claims:', error);
    }
  }
};

// Load draft notes from localStorage (for products added before quote creation)
const loadDraftNotes = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_notes_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftNotes = JSON.parse(stored);
      console.log(`📦 Loaded ${draftNotes.length} draft notes from localStorage`);
      
      const formattedNotes = draftNotes.map((note: any) => ({
        id: note.id,
        content: note.note_content,
        createdAt: new Date().toISOString(),
        isEditing: false,
        isTemp: true
      }));
      
      // Add to state
      setNotes(prev => {
        const existingIds = new Set(prev.map(n => n.id));
        const newNotes = formattedNotes.filter((note: any) => !existingIds.has(note.id));
        
        if (newNotes.length === 0) {
          console.log('No new draft notes to add');
          return prev;
        }
        
        console.log(`Adding ${newNotes.length} new draft notes`);
        return [...prev, ...newNotes];
      });
    } catch (error) {
      console.error('Error loading draft notes:', error);
    }
  }
};

// Load draft properties from localStorage (for products added before quote creation)
const loadDraftProperties = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_properties_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftProperties = JSON.parse(stored);
      console.log(`📦 Loaded ${draftProperties.length} draft properties from localStorage`);
      
      const formattedProperties = draftProperties.map((prop: any) => ({
        ...prop,
        isEditing: false,
        isTemp: true
      }));
      
      setProperties(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newProperties = formattedProperties.filter((prop: any) => !existingIds.has(prop.id));
        
        if (newProperties.length === 0) return prev;
        
        console.log(`Adding ${newProperties.length} new draft properties`);
        return [...prev, ...newProperties];
      });
    } catch (error) {
      console.error('Error loading draft properties:', error);
    }
  }
};

// Load draft household contents from localStorage
const loadDraftHouseholdContents = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_household_contents_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftContents = JSON.parse(stored);
      console.log(`📦 Loaded ${draftContents.length} draft household contents from localStorage`);
      
      const formattedContents = draftContents.map((content: any) => ({
        ...content,
        isEditing: false,
        isTemp: true
      }));
      
      setHouseholdContents(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newContents = formattedContents.filter((c: any) => !existingIds.has(c.id));
        
        if (newContents.length === 0) return prev;
        
        console.log(`Adding ${newContents.length} new draft household contents`);
        return [...prev, ...newContents];
      });
    } catch (error) {
      console.error('Error loading draft household contents:', error);
    }
  }
};

// Load draft vehicles from localStorage
// Load draft vehicles from localStorage
const loadDraftVehicles = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_vehicles_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftVehicles = JSON.parse(stored);
      console.log(`📦 Loaded ${draftVehicles.length} draft vehicles from localStorage`);
      
      // DON'T clear the draft storage - keep it until quote is saved
      // localStorage.removeItem(storageKey);
      
      const formattedVehicles = draftVehicles.map((vehicle: any) => ({
        ...vehicle,
        isEditing: false,
        isTemp: true
      }));
      
      setVehicles(prev => {
        const existingIds = new Set(prev.map(v => v.id));
        const newVehicles = formattedVehicles.filter((v: any) => !existingIds.has(v.id));
        
        if (newVehicles.length === 0) {
          console.log('No new draft vehicles to add');
          return prev;
        }
        
        console.log(`Adding ${newVehicles.length} new draft vehicles`);
        return [...prev, ...newVehicles];  // Append to preserve existing
      });
    } catch (error) {
      console.error('Error loading draft vehicles:', error);
    }
  }
};

// Load draft trailers/caravans from localStorage
const loadDraftTrailers = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_trailers_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftTrailers = JSON.parse(stored);
      console.log(`📦 Loaded ${draftTrailers.length} draft trailers/caravans from localStorage`);
      
      const formattedTrailers = draftTrailers.map((trailer: any) => ({
        ...trailer,
        insuredValue: trailer.insured_value || trailer.insuredValue || 0,
        isEditing: false,
        isTemp: true
      }));
      
      setTrailersCaravans(prev => {
        const existingIds = new Set(prev.map(t => t.id));
        const newTrailers = formattedTrailers.filter((t: any) => !existingIds.has(t.id));
        
        if (newTrailers.length === 0) {
          console.log('No new draft trailers to add');
          return prev;
        }
        
        console.log(`Adding ${newTrailers.length} new draft trailers`);
        return [...prev, ...newTrailers];
      });
    } catch (error) {
      console.error('Error loading draft trailers:', error);
    }
  }
};

// Load draft value added products from localStorage
const loadDraftValueAdded = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_value_added_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftProducts = JSON.parse(stored);
      console.log(`📦 Loaded ${draftProducts.length} draft value added products from localStorage`);
      
      const formattedProducts = draftProducts.map((product: any) => ({
        ...product,
        isEditing: false,
        isTemp: true
      }));
      
      setValueAddedProducts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newProducts = formattedProducts.filter((p: any) => !existingIds.has(p.id));
        if (newProducts.length === 0) return prev;
        console.log(`Adding ${newProducts.length} new draft value added products`);
        return [...prev, ...newProducts];
      });
    } catch (error) {
      console.error('Error loading draft value added products:', error);
    }
  }
};

// Load draft risk items from localStorage
const loadDraftRiskItems = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_risk_items_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftItems = JSON.parse(stored);
      console.log('📦 Raw draft risk items:', draftItems);
      
      const mapItemTypeForFrontend = (backendType: string): string => {
  const mapping: { [key: string]: string } = {
    'Jewelry': 'Jewelry',
    'Watch': 'Watches',
    'Camera': 'Cameras',
    'Laptop': 'Laptops',
    'Tablet': 'Tablets',
    'Mobile Device': 'Mobile Phones',
    'Tracking Device': 'Tracking Device',
    'Watch-Smart Mobile Device': 'Watch-Smart Mobile Device',
    'Unspecified': 'Unspecified',
    'Other': 'Other'
  };
  return mapping[backendType] || backendType;
};

const formattedItems = draftItems.map((item: any) => ({
  id: item.id,
  itemType: mapItemTypeForFrontend(item.item_type || item.itemType || ''),
  itemName: item.item_name || item.itemName || '',
  description: item.description || '',
  serialNumber: item.serial_number || item.serialNumber || '',
  coverType: item.cover_type || item.coverType || '',
  insuredValue: item.insured_value || item.insuredValue || 0,
  premium: item.premium || 0,
  excess: item.excess || 0,
  noClaimsBonus: item.no_claims_bonus || item.noClaimsBonus || 'None',
  isEditing: false,
  isTemp: true
}));
      
      console.log('📦 Formatted risk items:', formattedItems);
      
      setRiskItems(prev => {
        const existingIds = new Set(prev.map(r => r.id));
        const newItems = formattedItems.filter((r: any) => !existingIds.has(r.id));
        if (newItems.length === 0) return prev;
        console.log(`Adding ${newItems.length} new draft risk items`);
        return [...prev, ...newItems];
      });
    } catch (error) {
      console.error('Error loading draft risk items:', error);
    }
  }
};

// Load draft water crafts from localStorage
const loadDraftWaterCrafts = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_water_crafts_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftWaterCrafts = JSON.parse(stored);
      console.log(`📦 Loaded ${draftWaterCrafts.length} draft water crafts from localStorage`);
      
      const formattedWaterCrafts = draftWaterCrafts.map((wc: any) => ({
        ...wc,
        isEditing: false,
        isTemp: true
      }));
      
      setWaterCrafts(prev => {
        const existingIds = new Set(prev.map(w => w.id));
        const newWaterCrafts = formattedWaterCrafts.filter((w: any) => !existingIds.has(w.id));
        
        if (newWaterCrafts.length === 0) return prev;
        
        console.log(`Adding ${newWaterCrafts.length} new draft water crafts`);
        return [...prev, ...newWaterCrafts];
      });
    } catch (error) {
      console.error('Error loading draft water crafts:', error);
    }
  }
};

  // Load draft files from localStorage
const loadDraftFiles = () => {
  if (!clientId) return;
  
  const storageKey = `client_${clientId}_files_draft`;
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const draftFiles = JSON.parse(stored);
      console.log(`📦 Loaded ${draftFiles.length} draft files from localStorage`);
      
      const formattedFiles = draftFiles
        .filter((file: any) => file.file_id || file.alreadyUploaded) 
        .map((file: any) => ({
          id: file.id,
          file_name: file.file_name,
          file_size: file.file_size,
          file_type: file.file_type,
          description: file.description,
          document_type: file.document_type,
          isEditing: false,
          isTemp: false, // It's already on the server!
          file_id: file.file_id,
          alreadyUploaded: true
        }));
      
      setUploadedFiles(prev => {
        const existingIds = new Set(prev.map(f => f.file_id || f.id));
        const newFiles = formattedFiles.filter((f: any) => !existingIds.has(f.file_id));
        
        if (newFiles.length === 0) return prev;
        
        console.log(`Adding ${newFiles.length} already-uploaded files from localStorage`);
        return [...prev, ...newFiles];
      });
    } catch (error) {
      console.error('Error loading draft files:', error);
    }
  }
};

// Reset first load flag and clear products when navigating to a new quote (not edit mode and no refreshProducts)
useEffect(() => {
  if (!clientId) return;
  
  const isEditMode = editingQuoteId || location.state?.editingQuoteId;
  const shouldRefresh = location.state?.refreshProducts;
  
  // 🔍 Debug log to see what's happening
  console.log('🔍 Fresh reset check:', {
    isEditMode,
    shouldRefresh,
    preventFreshResetRef: preventFreshResetRef.current,
    editingQuoteId,
    locationStateRefresh: location.state?.refreshProducts
  });
  
  // ✅ FIX: Only reset if we are NOT refreshing (coming back from adding a product)
  const isReturningFromProduct = location.state?.refreshProducts === true;
  
  // ✅ NEW: Check if we are returning from a product page via Cancel button
  const isReturningFromCancel = location.state?.fromCancel === true || sessionStorage.getItem(`returning_from_product_${clientId}`) === 'true';

  if (!isEditMode && !shouldRefresh && !isReturningFromProduct && !isReturningFromCancel && !preventFreshResetRef.current) {
    console.log('🆕 Fresh quote detected - resetting first load flag and clearing products');
    
    // Clear the main products localStorage to prevent old data from loading
    localStorage.removeItem(`client_${clientId}_products`);
    
    // ✅ FIX: Clear the saved administrators/underwriters for fresh quotes
    localStorage.removeItem('temp_selected_administrators');
    localStorage.removeItem('temp_selected_underwriters');

    // ✅ FIX: Clear the state so the UI starts blank
    setSelectedAdministrators([]);
    setSelectedUnderwriters([]);
    
    // Clear sessionStorage drafts
    sessionStorage.removeItem(`draft_quote_ref_${clientId}`);
    sessionStorage.removeItem(`draft_quote_duration_${clientId}`);
    sessionStorage.removeItem(`draft_quote_admin_${clientId}`);
    sessionStorage.removeItem(`draft_quote_underwriter_${clientId}`);
    sessionStorage.removeItem(`draft_quote_declarations_${clientId}`);
    
    // ✅ CRITICAL: Clear ALL draft data for this client
    const draftKeys = [
      `client_${clientId}_previous_insurances_draft`,
      `client_${clientId}_previous_claims_draft`,
      `client_${clientId}_notes_draft`,
      `client_${clientId}_properties_draft`,
      `client_${clientId}_household_contents_draft`,
      `client_${clientId}_vehicles_draft`,
      `client_${clientId}_trailers_draft`,
      `client_${clientId}_value_added_draft`,
      `client_${clientId}_risk_items_draft`,
      `client_${clientId}_water_crafts_draft`,
      `client_${clientId}_files_draft`,
      `client_${clientId}_products`
    ];
    
    draftKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🗑️ Cleared draft: ${key}`);
      }
    });
    
    // Reset flags
    isFirstLoad.current = true;
    hasLoadedInitialProducts.current = false;
    
    // Clear all product states
    setProperties([]);
    setVehicles([]);
    setHouseholdContents([]);
    setTrailersCaravans([]);
    setValueAddedProducts([]);
    setRiskItems([]);
    setWaterCrafts([]);
    setPreviousClaims([]);
    setPreviousInsurances([]);
    setNotes([]);
    setUploadedFiles([]);
    
    setSaveStatus('info');
    setSaveMessage('Starting fresh quote - all draft data cleared');
    setTimeout(() => setSaveStatus('idle'), 3000);
  } else if (shouldRefresh || preventFreshResetRef.current || isReturningFromCancel) {
    console.log('🔄 Refresh, prevent, or cancel flag detected - preserving drafts');
    // Don't clear anything
    
    // ✅ Clean up the flag after 2 seconds so the user can start a fresh quote later
    if (isReturningFromCancel) {
      setTimeout(() => {
        sessionStorage.removeItem(`returning_from_product_${clientId}`);
      }, 2000);
    }
  }
}, [clientId, location.state?.refreshProducts, editingQuoteId, location.state?.editingQuoteId]);

// When clientId changes (switching to a different client), reset the first load flag
useEffect(() => {
  if (clientId) {
    // Reset first load flag for new client
    isFirstLoad.current = true;
    console.log('🔄 Client changed, resetting first load flag for client:', clientId);
  }
}, [clientId]);

// Replace the existing products useEffect with this simplified versio
useEffect(() => {
  const loadProducts = () => {
    if (!clientId) return;
    
    const isEditMode = editingQuoteId || location.state?.editingQuoteId;
    
    if (isEditMode) {
      console.log('✏️ Edit mode: Loading existing products');
      setIsLoadingProducts(false);
      return;
    }
    
    // Check if we need to reload drafts due to refreshProducts flag
    const shouldRefresh = location.state?.refreshProducts;
    
    // ONLY load products ONCE when component first mounts, OR when refreshProducts is true
    if (hasLoadedInitialProducts.current && !shouldRefresh) {
      console.log('✅ Products already loaded initially, skipping');
      return;
    }
    
    // Reset the flag if we're refreshing
    if (shouldRefresh) {
      console.log('🔄 Refresh flag detected - reloading drafts');
    }
    
    setIsLoadingProducts(true);
    
    // First, try to load existing drafts
    const fromLocalStorage = loadProductsFromLocalStorage();
    if (fromLocalStorage) {
      console.log('📦 Loaded existing products from localStorage');
    } else {
      console.log('💾 No existing products found in localStorage');
    }
    
    // Load all drafts (these will merge with existing state)
    loadDraftPreviousInsurances();
    loadDraftPreviousClaims();
    loadDraftNotes();
    loadDraftProperties();
    loadDraftHouseholdContents();
    loadDraftVehicles();
    loadDraftTrailers();
    loadDraftValueAdded();
    loadDraftRiskItems();
    loadDraftWaterCrafts();
    loadDraftFiles();
    
    hasLoadedInitialProducts.current = true;
    setIsLoadingProducts(false);
  };
  
  loadProducts();
  
  // Cleanup function - DO NOT clear drafts automatically
  return () => {
    console.log('📝 Component unmounting but preserving drafts for next session');
  };
}, [clientId, location.state?.refreshProducts]); // Add refreshProducts to dependency array

// Save to localStorage whenever products change
useEffect(() => {
  if (clientId && (properties.length > 0 || vehicles.length > 0 || householdContents.length > 0 || 
      trailersCaravans.length > 0 || valueAddedProducts.length > 0 || riskItems.length > 0 || 
      previousClaims.length > 0 || previousInsurances.length > 0 || notes.length > 0 || uploadedFiles.length > 0)) {
    // Only save with client ID (no quote ID yet for drafts)
    saveProductsToLocalStorage();
    console.log('💾 Saved draft products to localStorage');
  }
}, [properties, vehicles, householdContents, trailersCaravans, valueAddedProducts, riskItems, previousClaims, previousInsurances, notes, clientId]);

// useEffect to handle browser refresh/return
useEffect(() => {
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    // If there are unsaved products, warn user
    if (properties.length > 0 || vehicles.length > 0 || 
        householdContents.length > 0 || previousClaims.length > 0 ||
        notes.length > 0 || uploadedFiles.length > 0) {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [properties, vehicles, householdContents, previousClaims, notes, uploadedFiles]);

// Function to load existing vehicles for editing
const loadExistingVehiclesForEditing = async (quoteId: string) => {
  try {
    console.log('🚗 Loading existing vehicles for quote:', quoteId);
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/vehicles?quote_id=${quoteId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const existingVehicles = response.data.data || response.data || [];
    console.log(`📦 Loaded ${existingVehicles.length} existing vehicles for editing`);
    
    // Format vehicles for state (convert snake_case to camelCase if needed)
    const formattedVehicles = existingVehicles.map((vehicle: any) => ({
      id: vehicle.id,
      vehicle_type: vehicle.vehicle_type,
      registration_number: vehicle.registration_number,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      variant: vehicle.variant,
      color: vehicle.color,
      usage: vehicle.usage,
      vin_number: vehicle.vin_number,
      engine_number: vehicle.engine_number,
      mmcode: vehicle.mmcode,
      insured_value: vehicle.insured_value,
      cover_type: vehicle.cover_type,
      use_retail_value: vehicle.use_retail_value,
      excess_buy_back: vehicle.excess_buy_back,
      credit_shortfall: vehicle.credit_shortfall,
      car_hire: vehicle.car_hire,
      day_parking_location: vehicle.day_parking_location,
      night_parking_location: vehicle.night_parking_location,
      registered_owner: vehicle.registered_owner,
      regular_driver: vehicle.regular_driver,
      other_drivers: vehicle.other_drivers,
      safety_features: vehicle.safety_features,
      address_line1: vehicle.address_line1,
      address_line2: vehicle.address_line2,
      suburb: vehicle.suburb,
      city_town: vehicle.city_town,
      postal_code: vehicle.postal_code,
      region: vehicle.region,
      country: vehicle.country,
      overnight_parking_elsewhere: vehicle.overnight_parking_elsewhere,
      premium: vehicle.premium,
      excess: vehicle.excess,
      no_claim_bonus: vehicle.no_claim_bonus
    }));
    
    setVehicles(formattedVehicles);
    return formattedVehicles;
  } catch (error) {
    console.error('❌ Error loading existing vehicles:', error);
    return [];
  }
};

// Function to load all existing products for editing
const loadExistingProductsForEditing = async (quoteId: string) => {
  try {
    const token = localStorage.getItem('token');
    
    // Make sure the properties endpoint is returning all fields
    const propertyResponse = await axios.get(`${API_URL}/api/properties?quote_id=${quoteId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const propertiesData = propertyResponse.data.data || propertyResponse.data || [];
    
    // Log to check if all fields are present
    propertiesData.forEach((property: any, index: number) => {
      console.log(`🔍 Property ${index + 1} loaded:`, {
        id: property.id,
        hasThatchRoof: property.has_thatch_roof,
        thatchLessThan5m: property.thatch_less_than_5m,
        thatchSabsConductor: property.thatch_sabs_conductor,
        thatchTreated: property.thatch_treated,
        hasPowerSurgeCover: property.has_power_surge_cover,
        powerSurgeCoverValue: property.power_surge_cover_value,
        hasAccidentalDamageCover: property.has_accidental_damage_cover,
        accidentalDamageCoverValue: property.accidental_damage_cover_value
      });
    });
    
    // Map snake_case to camelCase
    const formattedProperties = propertiesData.map((property: any) => ({
  // Basic fields
  id: property.id,
  addressLine1: property.address_line_1,
  addressLine2: property.address_line_2,
  suburb: property.suburb,
  city: property.city,
  postalCode: property.postal_code,
  region: property.region,
  country: property.country,
  propertyType: property.property_type,
  residenceType: property.residence_type,
  buildingAge: property.building_age,
  situatedAt: property.situated_at,
  coverType: property.cover_type,
  insuredValue: property.insured_value,
  
  // Boolean fields - convert from 1/0 or 'true'/'false' strings to actual booleans
  hasSwimmingPool: Boolean(property.has_swimming_pool),
  hasMotorizedGate: Boolean(property.has_motorized_gate),
  isUnoccupiedMoreThan60Days: Boolean(property.is_unoccupied_more_than_60_days),
  isCommune: Boolean(property.is_commune),
  underConstruction: Boolean(property.under_construction),
  builtOnSteepSlope: Boolean(property.built_on_steep_slope),
  hasExcessiveRetainingWalls: Boolean(property.has_excessive_retaining_walls),
  nearWaterBody: Boolean(property.near_water_body),
  hasRisingDamp: Boolean(property.has_rising_damp),
  hasWallCracks: Boolean(property.has_wall_cracks),
  hasSubsidenceHistory: Boolean(property.has_subsidence_history),
  hasOutbuilding: Boolean(property.has_outbuilding),
  hasPerimeterFence: Boolean(property.has_perimeter_fence),
  hasElectricFence: Boolean(property.has_electric_fence),
  
  hasThatchRoof: Boolean(property.has_thatch_roof),
  thatchLessThan5m: Boolean(property.thatch_less_than_5m),
  thatchSabsConductor: Boolean(property.thatch_sabs_conductor),
  thatchTreated: Boolean(property.thatch_treated),
  
  hasAccidentalDamageCover: Boolean(property.has_accidental_damage_cover),
  accidentalDamageCoverValue: property.accidental_damage_cover_value,
  
  hasPowerSurgeCover: Boolean(property.has_power_surge_cover),
  powerSurgeCoverValue: property.power_surge_cover_value,
  
  // Array fields - parse JSON strings back to arrays
  wallMaterials: property.wall_materials ? JSON.parse(property.wall_materials) : [],
  roofMaterials: property.roof_materials ? JSON.parse(property.roof_materials) : [],
  floorMaterials: property.floor_materials ? JSON.parse(property.floor_materials) : [],
  otherSurroundingAreas: property.other_surrounding_areas ? JSON.parse(property.other_surrounding_areas) : [],
  
  // Other fields
  premium: property.premium,
  excess: property.excess,
  noClaimBonus: property.no_claim_bonus
}));
    
    setProperties(formattedProperties);

    // Load previous insurances
    console.log('📦 Loading previous insurances for quote:', quoteId);
    const previousInsuranceResponse = await axios.get(`${API_URL}/api/previous-insurances?quote_id=${quoteId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const insurancesData = previousInsuranceResponse.data.data || previousInsuranceResponse.data || [];
    console.log(`📦 Loaded ${insurancesData.length} previous insurances for editing`);
    
    // Format the data to match your PreviousInsurance interface
    const formattedInsurances = insurancesData.map((ins: any) => ({
      id: ins.id,
      description: ins.description,
      policyNumber: ins.policyNumber,
      insurer: ins.insurer,
      startDate: ins.startDate,
      endDate: ins.endDate,
      isEditing: false
    }));
    
    setPreviousInsurances(formattedInsurances);
    
  } catch (error) {
    console.error('❌ Error loading properties for editing:', error);
  }
};

// load existing quote data when in edit mode
useEffect(() => {
  const loadQuoteForEditing = async () => {
    // Get data from props OR location state
    const quoteId = editingQuoteId || location.state?.editingQuoteId;
    const quoteDataToUse = quoteData || location.state?.quoteData;
    
    if (quoteId && quoteDataToUse && clientId) {
      console.log('✏️ Loading quote for editing:', {
        quoteId,
        quoteData: quoteDataToUse,
        administrator_ids: quoteDataToUse.administrator_ids,
        underwriter_ids: quoteDataToUse.underwriter_ids,
        declarations: quoteDataToUse.declarations
      });

      // Clear any stale drafts from sessionStorage before loading
      sessionStorage.removeItem(`draft_quote_ref_${clientId}`);
      sessionStorage.removeItem(`draft_quote_duration_${clientId}`);
      sessionStorage.removeItem(`draft_quote_admin_${clientId}`);
      sessionStorage.removeItem(`draft_quote_underwriter_${clientId}`);
      sessionStorage.removeItem(`draft_quote_declarations_${clientId}`);
      
      // 1. Set quote reference - CRITICAL: Use the existing reference
      if (quoteDataToUse.reference || quoteDataToUse.referenceNumber) {
        const existingReference = quoteDataToUse.reference || quoteDataToUse.referenceNumber;
        setQuoteReference(existingReference);
        console.log('📝 Set quote reference to existing:', existingReference);
      }
      
      // 2. Set administrators - from the join table data
      if (quoteDataToUse.administrator_ids && Array.isArray(quoteDataToUse.administrator_ids)) {
        const adminIds = quoteDataToUse.administrator_ids.map(String);
        console.log('👥 Setting administrators:', adminIds);
        setSelectedAdministrators(adminIds);
      }

      // 3. Set underwriters - from the join table data
      if (quoteDataToUse.underwriter_ids && Array.isArray(quoteDataToUse.underwriter_ids)) {
        const uwIds = quoteDataToUse.underwriter_ids.map(String);
        console.log('👥 Setting underwriters:', uwIds);
        setSelectedUnderwriters(uwIds);
      }

      // Load declaration questions for edit mode
      if (quoteDataToUse.declaration_questions && quoteDataToUse.declaration_questions.length > 0) {
        console.log('📋 Loading declaration questions for editing:', quoteDataToUse.declaration_questions);
        
        // Create a new selectedDeclarations object
        const newSelectedDeclarations: {[key: number]: boolean} = {};
        
        // Get all declaration questions first
        try {
          const token = localStorage.getItem('token');
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          
          const response = await fetch(`${API_URL}/api/quotation/declaration-questions`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const allQuestions = await response.json();
            
            // Initialize all as unchecked
            allQuestions.forEach((question: DeclarationQuestion) => {
              newSelectedDeclarations[question.id] = false;
            });
            
            // Check the ones that are in the quote data
            quoteDataToUse.declaration_questions.forEach((question: any) => {
              const questionId = typeof question === 'object' ? question.id : question;
              newSelectedDeclarations[questionId] = true;
            });
            
            console.log('✅ Set selected declarations for editing:', newSelectedDeclarations);
            setSelectedDeclarations(newSelectedDeclarations);
          }
        } catch (error) {
          console.error('❌ Error loading declaration questions for edit:', error);
          
          // Fallback: just check the ones we know are selected
          const fallbackSelected: {[key: number]: boolean} = {};
          quoteDataToUse.declaration_questions.forEach((question: any) => {
            const questionId = typeof question === 'object' ? question.id : question;
            fallbackSelected[questionId] = true;
          });
          setSelectedDeclarations(fallbackSelected);
        }
      } else {
        console.log('ℹ️ No declaration questions found in quote data');
      }

      // ✅ FIX: Call loadExistingProducts which loads ALL product types
      await loadExistingProducts(quoteId, clientId);
      
      // Set isEditing to true
      setIsEditing(true);
    }
  };

  loadQuoteForEditing();
}, [editingQuoteId, quoteData, clientId, location.state]);

  // handler functions for consultants
  const handleAssignConsultantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAssignToDifferentConsultant(e.target.checked);
    if (!e.target.checked) {
      setSelectedConsultant(''); // Reset selection when unchecked
    }
  };

  const handleConsultantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedConsultant(e.target.value);
  };

    if (!can('quotes', 'read')) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to access Quotes.</p>
      </div>
    );
  }


  if (!clientId) {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      Error: Invalid client ID
    </div>
  );
}

  if (isLoading || !user) {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      Loading quote creation page...
    </div>
  );
}

  const prepareQuoteData = (): CreateQuoteRequest => {
  console.log('🔍 prepareQuoteData - Selected values:', {
    selectedAdministrators,
    selectedUnderwriters
  });

  // Get the selected declaration questions
  const selectedQuestionIds = Object.entries(selectedDeclarations)
    .filter(([_, isSelected]) => isSelected)
    .map(([questionId]) => parseInt(questionId));

  // Get the actual question texts
  const selectedQuestions = declarationQuestions
    .filter(q => selectedDeclarations[q.id])
    .map(q => q.question);

  console.log('🔍 Selected declaration questions:', {
    ids: selectedQuestionIds,
    questions: selectedQuestions
  });

  // When editing, use the existing quote reference
  const quoteDataFromProps = quoteData || location.state?.quoteData;
  
  let uniqueReference = quoteReference;
  
  // If we're editing and have existing quote data, use that reference
  if (isEditing && quoteDataFromProps) {
    uniqueReference = quoteDataFromProps.reference || 
                    quoteDataFromProps.referenceNumber || 
                    quoteReference;
    console.log('✏️ EDIT MODE: Using existing quote reference:', uniqueReference);
  }
  
  // Only generate new reference for NEW quotes
  if (!uniqueReference && !isEditing) {
    const generateUniqueReference = () => {
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).substr(2, 5);
      return `Q-${clientId}-${timestamp}-${random}`.toUpperCase();
    };
    uniqueReference = generateUniqueReference();
    console.log('➕ NEW QUOTE: Generated new reference:', uniqueReference);
  }

  console.log('🔍 Final quote reference:', uniqueReference);

  // Get the first administrator and underwriter if available
  const primaryAdministratorId = selectedAdministrators.length > 0 
    ? parseInt(selectedAdministrators[0]) 
    : undefined;
    
  const primaryUnderwriterId = selectedUnderwriters.length > 0 
    ? parseInt(selectedUnderwriters[0]) 
    : undefined;

  console.log('🔍 Primary IDs:', {
    primaryAdministratorId,
    primaryUnderwriterId
  });

  // DEBUG LOG:
  console.log('🚨 DEBUG: Administrator IDs being sent:', {
    selectedAdministrators,
    count: selectedAdministrators.length,
    ids: selectedAdministrators.map(id => parseInt(id))
  });

  // Map risk items for backend
const mappedRiskItems = riskItems.map(item => {
  // Handle undefined noClaimsBonus
  const noClaimsBonusValue = item.noClaimsBonus || 'None';
  const backendNoClaimsBonus = mapNoClaimsBonusForBackend(noClaimsBonusValue);
  
  return {
    itemType: item.itemType || '',
    description: item.description || '',
    itemName: item.itemName || '',
    serialNumber: item.serialNumber || '',
    coverType: item.coverType || '',
    noClaimsBonus: backendNoClaimsBonus,
    insuredValue: parseFloat(item.insuredValue) || 0,
    premium: parseFloat(item.premium) || 0,
    excess: parseFloat(item.excess) || 0,
    client_id: parseInt(clientId),
    quote_id: null
  };
});

  console.log('🔍 Mapped risk items for backend (camelCase):', mappedRiskItems);

  // Map waterCrafts to snake_case for backend
const mappedWaterCrafts = waterCrafts.map(wc => ({
  year: wc.year ? Number(wc.year) : null,
  craft_type: wc.craftType || null,
  hull_make: wc.hullMake || null,
  hull_length: wc.hullLength ? Number(wc.hullLength) : null,
  year_model: wc.yearModel ? Number(wc.yearModel) : null,
  body_paint: wc.bodyPaint || null,
  outboard_motor_make: wc.outboardMotorMake || null,
  outboard_engine_number: wc.outboardEngineNumber || null,
  horse_power: wc.horsePower ? Number(wc.horsePower) : null,
  max_speed: wc.maxSpeed ? Number(wc.maxSpeed) : null,
  vessel_name: wc.vesselName || null,
  vessel_storage: wc.vesselStorage || null,
  vessel_usage: wc.vesselUsage || null,
  amateur_built: wc.amateurBuilt || false,
  registered_owner: wc.registeredOwner ? String(wc.registeredOwner).replace('poi-', '') : null,
  finance_house: wc.financeHouse || null,
  finance_branch: wc.financeBranch || null,
  hull_value: wc.hullValue ? Number(wc.hullValue) : 0,
  outboard_value: wc.outboardValue ? Number(wc.outboardValue) : 0,
  sails_value: wc.sailsValue ? Number(wc.sailsValue) : 0,
  total_sum_insured: wc.totalSumInsured ? Number(wc.totalSumInsured) : 0,
  total_monthly_premium: wc.totalMonthlyPremium ? Number(wc.totalMonthlyPremium) : 0,
  trailer_make: wc.trailerMake || null,
  trailer_model: wc.trailerModel || null,
  trailer_registration: wc.trailerRegistration || null,
  trailer_value: wc.trailerValue ? Number(wc.trailerValue) : 0,
  trailer_premium: wc.trailerPremium ? Number(wc.trailerPremium) : 0,
  client_id: parseInt(clientId),
  quote_id: null,
}));

console.log('🚤 Mapped watercraft for payload:', mappedWaterCrafts.length);

  // using quotePayload instead of quoteData to avoid naming conflict
  const quotePayload = {
  reference: uniqueReference,
  status: 'draft',
  client_id: parseInt(clientId),
  administrator_ids: selectedAdministrators.length > 0 ? selectedAdministrators.map(id => parseInt(id)) : [],
  underwriter_ids: selectedUnderwriters.length > 0 ? selectedUnderwriters.map(id => parseInt(id)) : [],
  product_id: selectedProduct ? parseInt(selectedProduct) : undefined,
  duration: selectedDuration ? parseInt(selectedDuration) : undefined,
  notes: notes.map(note => note.content).join('\n\n'),
  declaration_questions: selectedQuestionIds,
  declaration_question_texts: selectedQuestions,
  water_crafts: waterCrafts,
  vehicles: isEditing ? undefined : vehicles,
  properties: properties,
  consultant_id: assignToDifferentConsultant && selectedConsultant ? parseInt(selectedConsultant) : undefined,
  risk_items: mappedRiskItems,
  household_contents: householdContents,
  trailers_caravans: trailersCaravans,
  value_added_products: valueAddedProducts,
  previous_claims: previousClaims,
  previous_insurances: previousInsurances,
  created_by: user?.id || null
};

// ADD THIS DEBUG LOG RIGHT BEFORE RETURN
console.log('🚀 SENDING TO BACKEND:', {
  administrator_ids: quotePayload.administrator_ids,
  administrator_ids_type: typeof quotePayload.administrator_ids,
  administrator_ids_isArray: Array.isArray(quotePayload.administrator_ids),
  administrator_ids_length: quotePayload.administrator_ids.length,
  underwriter_ids: quotePayload.underwriter_ids,
  selectedAdministrators: selectedAdministrators,
  selectedUnderwriters: selectedUnderwriters
});

return quotePayload as CreateQuoteRequest;

};

const handleSaveQuote = async (isDraft: boolean = false) => {
  console.log('🔴🔴🔴 SAVE QUOTE - SELECTED ADMINISTRATORS:', selectedAdministrators);
  console.log('🔴🔴🔴 SAVE QUOTE - ADMINISTRATORS COUNT:', selectedAdministrators.length);
  console.log('🔴🔴🔴 SAVE QUOTE - ADMINISTRATORS TYPE:', typeof selectedAdministrators);
  console.log('🔴🔴🔴 SAVE QUOTE - IS ARRAY:', Array.isArray(selectedAdministrators));
  console.log('📊 DEBUG: Current state before save:', {
    trailersCaravansCount: trailersCaravans.length,
    trailersCaravans: trailersCaravans,
    trailersCaravansData: JSON.stringify(trailersCaravans, null, 2),
    isEditing,
    editingQuoteId,
    editingQuoteIdState
  });


  try {
    const quoteData = prepareQuoteData();
    quoteData.status = isDraft ? 'draft' : 'sent';

    console.log('🚀 Sending quote data to backend:', quoteData);

    // DEBUG TO SEE EXACTLY WHAT'S BEING SENT:
    console.log('📤 DEBUG PAYLOAD TO BACKEND:', {
      fullPayload: JSON.stringify(quoteData, null, 2),
      administrator_ids: quoteData.administrator_ids,
      administrator_id: quoteData.administrator_id,
      administrator_ids_count: quoteData.administrator_ids?.length || 0
    });

    // ✅ FIX: Get editing quote ID from props OR location state
    const editingQuoteIdToUse = editingQuoteId || location.state?.editingQuoteId;
    
    // ✅ FIX: Check if we're editing AND have a quote ID
    console.log('🔍 Save check:', {
      isEditing,
      editingQuoteId,
      editingQuoteIdToUse,
      locationState: location.state
    });
    
    if (isEditing && editingQuoteIdToUse) {
      console.log('✏️ Updating existing quote:', editingQuoteIdToUse);
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          `${API_URL}/api/quotes/${editingQuoteIdToUse}`,
          quoteData,
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        console.log('✅ Quote updated successfully:', response.data);
        
        if (response.data.success && response.data.data?.id) {
          const quoteId = response.data.data.id;

          // ✅ LOG ACTIVITY: Quote Updated
try {
  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('user');
  let performedBy = 'System';
  
  if (userData) {
    try {
      const user = JSON.parse(userData);
      const firstName = user.first_name || user.name || '';
      const lastName = user.last_name || user.surname || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      if (fullName) {
        performedBy = fullName;
      } else if (user.username) {
        performedBy = user.username;
      } else if (user.email) {
        performedBy = user.email;
      }
    } catch (parseError) {
      console.error('Error parsing user data:', parseError);
    }
  }
  
  await axios.post(
  `${API_URL}/api/quote-activity-logs`,
  {
    quote_id: quoteId,
    action_type: 'quote_updated',
    description: `Quote updated: ${quoteReference}`,
    performed_by: performedBy,  // ✅ ADD THIS - Top level field
    metadata: {
      clientId: clientId,
      quoteReference: quoteReference,
      updatedAt: new Date().toISOString(),
      performedBy: performedBy
    }
  },
  { headers: { Authorization: `Bearer ${token}` } }
);
  console.log(`✅ Activity logged: quote_updated by ${performedBy}`);
} catch (logError) {
  console.error('❌ Failed to log quote update activity:', logError);
}
          
          // ✅ Save products to database with the quote ID
          await saveAllProductsToDatabase(quoteId);
          
          // Clear draft previous insurances from localStorage
          localStorage.removeItem(`client_${clientId}_previous_insurances_draft`);
          // Clear draft previous claims from localStorage
          localStorage.removeItem(`client_${clientId}_previous_claims_draft`);
          // Clear draft notes from localStorage
          localStorage.removeItem(`client_${clientId}_notes_draft`);
          // Clear draft properties from localStorage
          localStorage.removeItem(`client_${clientId}_properties_draft`);
          // Clear draft household contents from localStorage
          localStorage.removeItem(`client_${clientId}_household_contents_draft`);
          // Clear draft vehicles from localStorage
          localStorage.removeItem(`client_${clientId}_vehicles_draft`);
          localStorage.removeItem(`client_${clientId}_trailers_draft`);
          localStorage.removeItem(`client_${clientId}_value_added_draft`);
          localStorage.removeItem(`client_${clientId}_risk_items_draft`);
          localStorage.removeItem(`client_${clientId}_water_crafts_draft`);
          localStorage.removeItem(`client_${clientId}_files_draft`);
          
          // Clear states
          setProperties([]);
          setVehicles([]);
          setHouseholdContents([]);
          setTrailersCaravans([]);
          setValueAddedProducts([]);
          setRiskItems([]);
          setPreviousClaims([]);
          setPreviousInsurances([]);
          setNotes([]);
          setUploadedFiles([]);
          
          // Clear the draft from sessionStorage after successful save
          sessionStorage.removeItem(`draft_quote_ref_${clientId}`);
          sessionStorage.removeItem(`draft_quote_duration_${clientId}`);
          sessionStorage.removeItem(`draft_quote_admin_${clientId}`);
          sessionStorage.removeItem(`draft_quote_underwriter_${clientId}`);
          sessionStorage.removeItem(`draft_quote_declarations_${clientId}`);

          // Navigate back to quotes tab view
          setTimeout(() => {
            navigate(`/personal/${clientId}`, { 
              state: { 
                activeTab: 'quotes',
                updatedQuoteId: quoteId
              } 
            });
          }, 1500);
        }
      } catch (updateError) {
        console.error('❌ Error updating quote:', updateError);
        setSaveStatus('error');
        setSaveMessage('Failed to update quote');
        throw updateError;
      }
    } else {
      console.warn('⚠️ Creating NEW quote (not updating) because:', {
        isEditing,
        editingQuoteId,
        editingQuoteIdToUse: editingQuoteIdToUse,
        hasLocationState: !!location.state
      });
      // Original create new quote logic
      console.log('🚀 Creating new quote...');
      
      const response = await createQuote(quoteData);

      console.log('✅ QUOTE CREATION RESPONSE:', response);

      if (response?.success && response.data?.id) {
        const quoteId = response.data.id;
        setCurrentQuoteId(quoteId);

        // ✅ LOG ACTIVITY: Quote Created
try {
  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('user');
  let performedBy = 'System';

  if (userData) {
    try {
      const user = JSON.parse(userData);
      const firstName = user.first_name || user.name || '';
      const lastName = user.last_name || user.surname || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      if (fullName) {
        performedBy = fullName;
      } else if (user.username) {
        performedBy = user.username;
      } else if (user.email) {
        performedBy = user.email;
      }
    } catch (parseError) {
      console.error('Error parsing user data:', parseError);
    }
  }
    
  await axios.post(
  `${API_URL}/api/quote-activity-logs`,
  {
    quote_id: quoteId,
    action_type: 'quote_created',
    description: `New quote created: ${quoteReference}`,
    performed_by: performedBy,  // ✅ ADD THIS - Top level field
    metadata: {
      clientId: clientId,
      quoteReference: quoteReference,
      createdDate: new Date().toISOString(),
      status: 'draft',
      performedBy: performedBy
    }
  },
  { headers: { Authorization: `Bearer ${token}` } }
);
  console.log(`✅ Activity logged: quote_created by ${performedBy}`);
} catch (logError) {
  console.error('❌ Failed to log quote creation activity:', logError);
}

        // ✅ Save products to database with the quote ID
        await saveAllProductsToDatabase(quoteId);
        
        // Clear draft previous insurances from localStorage
        localStorage.removeItem(`client_${clientId}_previous_insurances_draft`);
        // Clear draft previous claims from localStorage
        localStorage.removeItem(`client_${clientId}_previous_claims_draft`);
        // Clear draft notes from localStorage
        localStorage.removeItem(`client_${clientId}_notes_draft`);
        // Clear draft properties from localStorage
        localStorage.removeItem(`client_${clientId}_properties_draft`);
        // Clear draft household contents from localStorage
        localStorage.removeItem(`client_${clientId}_household_contents_draft`);
        // Clear draft vehicles from localStorage
        localStorage.removeItem(`client_${clientId}_vehicles_draft`);
        localStorage.removeItem(`client_${clientId}_trailers_draft`);
        localStorage.removeItem(`client_${clientId}_value_added_draft`);
        localStorage.removeItem(`client_${clientId}_risk_items_draft`);
        localStorage.removeItem(`client_${clientId}_water_crafts_draft`);
        localStorage.removeItem(`client_${clientId}_files_draft`);
        
        // Clear states
        setProperties([]);
        setVehicles([]);
        setHouseholdContents([]);
        setTrailersCaravans([]);
        setValueAddedProducts([]);
        setRiskItems([]);
        setPreviousClaims([]);
        setPreviousInsurances([]);
        setNotes([]);
        setUploadedFiles([]);
        
        // Clear the draft from sessionStorage after successful save
        sessionStorage.removeItem(`draft_quote_ref_${clientId}`);
        sessionStorage.removeItem(`draft_quote_duration_${clientId}`);
        sessionStorage.removeItem(`draft_quote_admin_${clientId}`);
        sessionStorage.removeItem(`draft_quote_underwriter_${clientId}`);
        sessionStorage.removeItem(`draft_quote_declarations_${clientId}`);

        // Navigate to quotes tab view
        setTimeout(() => {
          navigate(`/personal/${clientId}`, { 
            state: { 
              activeTab: 'quotes',
              newlyCreatedQuoteId: quoteId
            } 
          });
        }, 1500);
        
      } else {
        setSaveStatus('error');
        setSaveMessage(response?.message || 'Failed to save quote');
      }
    }
  } catch (err) {
    setSaveStatus('error');
    setSaveMessage('An error occurred while saving the quote and products');
    console.error('Error saving quote:', err);
  }
};

const uploadQuoteFiles = async (quoteId: number, files: UploadedFile[], clientId: string) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const errors: string[] = [];
  const successfulUploads: any[] = [];

  // Files are already uploaded immediately when selected
  // We just need to associate them with the quote
  const fileIdsToAssociate = files
    .filter(f => (f as any).file_id && (f as any).alreadyUploaded)
    .map(f => parseInt((f as any).file_id));
  
  if (fileIdsToAssociate.length > 0) {
    try {
      console.log(`🔗 Associating ${fileIdsToAssociate.length} pre-uploaded files with quote ${quoteId}`);
      
      const response = await axios.post(
        `${API_URL}/api/quotes/associate-temp-files`,
        { quoteId, fileIds: fileIdsToAssociate },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      console.log(`✅ Associated ${response.data.associatedFiles?.length || fileIdsToAssociate.length} files with quote`);
      successfulUploads.push(...(response.data.associatedFiles || []));
    } catch (error: any) {
      console.error('❌ Error associating files with quote:', error);
      errors.push(`Failed to associate ${fileIdsToAssociate.length} files with quote: ${error.message}`);
    }
  }

  return { errors, successfulUploads };
};

  // Function to save all products to database
const saveAllProductsToDatabase = async (quoteId: number) => {
  console.log('💾 DEBUG saveAllProductsToDatabase - Starting');
  console.log('📦 DEBUG: Products to save:', {
    quoteId,
    trailersCaravansCount: trailersCaravans.length,
    trailersCaravans: trailersCaravans,
    allProducts: {
      propertiesCount: properties.length,
      vehiclesCount: vehicles.length,
      householdContentsCount: householdContents.length,
      trailersCaravansCount: trailersCaravans.length,
      valueAddedProductsCount: valueAddedProducts.length,
      riskItemsCount: riskItems.length,
      waterCraftsCount: waterCrafts.length,
      previousClaimsCount: previousClaims.length,
      notesCount: notes.length,
      uploadedFilesCount: uploadedFiles.length
    }
  });


  const lockKey = `quote_save_lock_${quoteId}`;
  if (sessionStorage.getItem(lockKey)) {
    console.warn("🚨 Preventing DOUBLE SAVE attempt for quote", quoteId);
    return; // Exit early - already saving
  }

  sessionStorage.setItem(lockKey, "true");
  console.log("🔒 Acquired save lock for quote", quoteId);

  const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Declare errors array at the top of the function
    const errors: string[] = [];

    try {
      // Handle edit mode - delete old products if needed
      if (isEditing && editingQuoteId) {
        console.log('✏️ EDIT MODE: Processing products for quote update:', quoteId);
      }

      // ✅ Save selected declaration questions
      const selectedQuestionIds = Object.entries(selectedDeclarations)
        .filter(([_, isSelected]) => isSelected)
        .map(([questionId]) => parseInt(questionId));
    
      if (selectedQuestionIds.length > 0) {
        console.log(`✅ Declaration questions already saved in quote creation: ${selectedQuestionIds.length} questions`);
        console.log('📋 Declaration question IDs:', selectedQuestionIds);
      } else {
        console.log('ℹ️ No declaration questions selected');
      }

      // Save properties
if (properties.length > 0) {
  console.log(`💾 Saving ${properties.length} properties for quote ${quoteId}...`);
  
  for (const property of properties) {
    try {
      // In edit mode, check if we should update or create new
      if (property.id && !property.id.toString().startsWith('temp-')) {
        // This is an existing property - UPDATE it
        console.log('🔄 Updating existing property:', property.id);
        
        const propertyUpdateData = {
          addressLine1: property.addressLine1 || '',
          addressLine2: property.addressLine2 || '',
          suburb: property.suburb || '',
          city: property.city || '',
          postalCode: property.postalCode || '',
          region: property.region || '',
          country: property.country || 'South Africa',
          propertyType: property.propertyType || '',
          residenceType: property.residenceType || '',
          buildingAge: property.buildingAge || '',
          situatedAt: property.situatedAt || 'Client Residence',
          coverType: property.coverType || 'Comprehensive',
          insuredValue: Number(property.insuredValue) || 0,
          hasSwimmingPool: property.hasSwimmingPool || false,
          hasMotorizedGate: property.hasMotorizedGate || false,
          isUnoccupiedMoreThan60Days: property.isUnoccupiedMoreThan60Days || false,
          isCommune: property.isCommune || false,
          underConstruction: property.underConstruction || false,
          builtOnSteepSlope: property.builtOnSteepSlope || false,
          hasExcessiveRetainingWalls: property.hasExcessiveRetainingWalls || false,
          nearWaterBody: property.nearWaterBody || false,
          hasRisingDamp: property.hasRisingDamp || false,
          hasWallCracks: property.hasWallCracks || false,
          hasSubsidenceHistory: property.hasSubsidenceHistory || false,
          hasOutbuilding: property.hasOutbuilding || false,
          hasPerimeterFence: property.hasPerimeterFence || false,
          hasElectricFence: property.hasElectricFence || false,
          hasThatchRoof: property.hasThatchRoof || false,
          thatchLessThan5m: property.thatchLessThan5m || false,
          thatchSabsConductor: property.thatchSabsConductor || false,
          thatchTreated: property.thatchTreated || false,
          hasAccidentalDamageCover: property.hasAccidentalDamageCover || false,
          accidentalDamageCoverValue: property.accidentalDamageCoverValue || 0,
          hasPowerSurgeCover: property.hasPowerSurgeCover || false,
          powerSurgeCoverValue: property.powerSurgeCoverValue || 0,
          // ✅ FIX: Send arrays as-is (already JSON strings from frontend)
          wallMaterials: property.wallMaterials || [],
          roofMaterials: property.roofMaterials || [],
          floorMaterials: property.floorMaterials || [],
          otherSurroundingAreas: property.otherSurroundingAreas || [],
          premium: property.premium || '',
          excess: Number(property.excess) || 0,
          noClaimBonus: property.noClaimBonus || '',
          quote_id: quoteId
        };
        
        const response = await axios.put(
          `${API_URL}/api/properties/${property.id}`,
          propertyUpdateData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        console.log('✅ Property updated:', response.data);
        
      } else {
        // This is a new property - CREATE it
        console.log('💾 Creating new property for quote:', quoteId);
        
        const clientIdNum = parseInt(clientId!);
        if (isNaN(clientIdNum) || clientIdNum <= 0) {
          console.error('❌ Cannot save property — invalid client ID', { clientId, clientIdNum });
          errors.push('Property: Missing or invalid client ID');
          continue;
        }
        
        // ✅ FIX: Send arrays as-is (already JSON strings from frontend)
        const propertyData = {
          addressLine1: property.addressLine1 || '',
          addressLine2: property.addressLine2 || '',
          suburb: property.suburb || '',
          city: property.city || '',
          postalCode: property.postalCode || '',
          region: property.region || '',
          country: property.country || 'South Africa',
          propertyType: property.propertyType || '',
          residenceType: property.residenceType || '',
          buildingAge: property.buildingAge || '',
          situatedAt: property.situatedAt || 'Client Residence',
          coverType: property.coverType || 'Comprehensive',
          insuredValue: Number(property.insuredValue) || 0,
          hasSwimmingPool: property.hasSwimmingPool || false,
          hasMotorizedGate: property.hasMotorizedGate || false,
          isUnoccupiedMoreThan60Days: property.isUnoccupiedMoreThan60Days || false,
          isCommune: property.isCommune || false,
          underConstruction: property.underConstruction || false,
          builtOnSteepSlope: property.builtOnSteepSlope || false,
          hasExcessiveRetainingWalls: property.hasExcessiveRetainingWalls || false,
          nearWaterBody: property.nearWaterBody || false,
          hasRisingDamp: property.hasRisingDamp || false,
          hasWallCracks: property.hasWallCracks || false,
          hasSubsidenceHistory: property.hasSubsidenceHistory || false,
          hasOutbuilding: property.hasOutbuilding || false,
          hasPerimeterFence: property.hasPerimeterFence || false,
          hasElectricFence: property.hasElectricFence || false,
          hasThatchRoof: property.hasThatchRoof || false,
          thatchLessThan5m: property.thatchLessThan5m || false,
          thatchSabsConductor: property.thatchSabsConductor || false,
          thatchTreated: property.thatchTreated || false,
          hasAccidentalDamageCover: property.hasAccidentalDamageCover || false,
          accidentalDamageCoverValue: property.accidentalDamageCoverValue || 0,
          hasPowerSurgeCover: property.hasPowerSurgeCover || false,
          powerSurgeCoverValue: property.powerSurgeCoverValue || 0,
          // ✅ FIX: Send arrays as-is (already JSON strings from frontend)
          wallMaterials: property.wallMaterials || [],
          roofMaterials: property.roofMaterials || [],
          floorMaterials: property.floorMaterials || [],
          otherSurroundingAreas: property.otherSurroundingAreas || [],
          premium: property.premium || '',
          excess: Number(property.excess) || 0,
          noClaimBonus: property.noClaimBonus || '',
          client_id: clientIdNum,
          quote_id: quoteId
        };
        
        console.log('📤 Sending property data to backend:', JSON.stringify(propertyData, null, 2));
        
        const response = await axios.post(`${API_URL}/api/properties`, propertyData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('✅ Property saved successfully:', response.data);
      }
      
    } catch (error) {
      console.error('❌ Error saving property:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('🔍 Server response status:', error.response.status);
        console.error('🔍 Server response data:', error.response.data);
      }
      errors.push(`Property: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
} else {
  console.log('ℹ️ No properties to save');
}

      // Save vehicles
      if (vehicles.length > 0) {
  console.log("🚗 VEHICLE SAVE PROCESS STARTED");
  console.log("Total vehicles in local state:", vehicles.length);
  
  // Step 1: Check what vehicles already exist in the database
  let existingVehiclesInDB: any[] = [];
  
  try {
    console.log("🔍 Checking database for existing vehicles for quote:", quoteId);
    const existingResponse = await axios.get(`${API_URL}/api/vehicles?quote_id=${quoteId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    existingVehiclesInDB = existingResponse.data.data || existingResponse.data || [];
    console.log(`📋 Found ${existingVehiclesInDB.length} existing vehicles in database`);
    
  } catch (error) {
    console.warn("⚠️ Could not fetch existing vehicles from database:", error);
  }
  
  // Step 2: Create a map of existing vehicles by composite key
  const existingVehicleMap = new Map<string, any>();
  existingVehiclesInDB.forEach((v: any) => {
    const key = `${(v.registration_number || "").trim().toUpperCase()}|${(v.make || "").trim().toUpperCase()}|${(v.model || "").trim().toUpperCase()}|${v.year || ""}|${(v.vin_number || "").trim().toUpperCase()}`;
    existingVehicleMap.set(key, v);
  });
  
  // Step 3: Process each vehicle
  for (const vehicle of vehicles) {
    try {
      // Create the composite key for this vehicle
      const vehicleKey = `${(vehicle.registration_number || "").trim().toUpperCase()}|${(vehicle.make || "").trim().toUpperCase()}|${(vehicle.model || "").trim().toUpperCase()}|${vehicle.year || ""}|${(vehicle.vin_number || "").trim().toUpperCase()}`;
      
      console.log('🔑 Processing vehicle:', {
        key: vehicleKey,
        id: vehicle.id,
        reg: vehicle.registration_number
      });
      
      const vehicleData = Object.fromEntries([
        ['client_id', parseInt(clientId!)],
        ['quote_id', quoteId],
        ['vehicle_type', vehicle.vehicle_type ?? ''],
        ['registration_number', vehicle.registration_number ?? ''],
        ['make', vehicle.make ?? ''],
        ['model', vehicle.model ?? ''],
        ['year', vehicle.year != null ? String(vehicle.year) : null],
        ['variant', vehicle.variant ?? ''],
        ['color', vehicle.color ?? ''],
        ['usage', vehicle.usage ?? ''],
        ['vin_number', vehicle.vin_number ?? ''],
        ['engine_number', vehicle.engine_number ?? ''],
        ['mmcode', vehicle.mmcode ?? ''],
        ['insured_value', vehicle.insured_value ?? '0'],
        ['cover_type', vehicle.cover_type ?? ''],
        ['use_retail_value', !!vehicle.use_retail_value],
        ['excess_buy_back', !!vehicle.excess_buy_back],
        ['credit_shortfall', !!vehicle.credit_shortfall],
        ['car_hire', !!vehicle.car_hire],
        ['day_parking_location', vehicle.day_parking_location ?? ''],
        ['night_parking_location', vehicle.night_parking_location ?? ''],
        ['registered_owner', vehicle.registered_owner ?? `client-${clientId}`],
        ['regular_driver', vehicle.regular_driver ?? ''],
        ['other_drivers', vehicle.other_drivers ?? ''],
        ['safety_features', vehicle.safety_features ?? ''],
        ['address_line1', vehicle.address_line1 ?? ''],
        ['address_line2', vehicle.address_line2 ?? ''],
        ['suburb', vehicle.suburb ?? ''],
        ['city_town', vehicle.city_town ?? ''],
        ['postal_code', vehicle.postal_code ?? ''],
        ['region', vehicle.region ?? ''],
        ['country', vehicle.country ?? 'South Africa'],
        ['overnight_parking_elsewhere', !!vehicle.overnight_parking_elsewhere],
        ['overnight_address_line1', (vehicle as any).overnight_address_line1 ?? ''],
        ['overnight_address_line2', (vehicle as any).overnight_address_line2 ?? ''],
        ['overnight_suburb', (vehicle as any).overnight_suburb ?? ''],
        ['overnight_city_town', (vehicle as any).overnight_city_town ?? ''],
        ['overnight_postal_code', (vehicle as any).overnight_postal_code ?? ''],
        ['overnight_region', (vehicle as any).overnight_region ?? ''],
        ['overnight_country', (vehicle as any).overnight_country ?? ''],
        ['premium', vehicle.premium ?? '0'],
        ['excess', vehicle.excess ?? '0'],
        ['no_claim_bonus', vehicle.no_claim_bonus ?? '']
      ]);
      
      // DECISION LOGIC:
      // 1. If vehicle already has a real database ID -> UPDATE
      // 2. If vehicle exists in database (by composite key) -> UPDATE
      // 3. Otherwise -> CREATE NEW
      
      const existingVehicle = existingVehicleMap.get(vehicleKey);
      const vehicleId = vehicle.id?.toString();
      const isTempId = vehicleId?.startsWith('temp-');
      
      if (vehicleId && !isTempId) {
        // Vehicle already has a real database ID
        console.log('🔄 Updating existing vehicle by ID:', vehicleId);
        await axios.put(
          `${API_URL}/api/vehicles/${vehicleId}`,
          vehicleData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        console.log('✅ Vehicle updated successfully');
      } else if (existingVehicle) {
        // Vehicle exists in database (matched by composite key)
        console.log('🔄 Updating existing vehicle found in database:', existingVehicle.id);
        console.log('🚗 DEBUG PUT REQUEST:', {
          url: `${API_URL}/api/vehicles/${existingVehicle.id}`,
          vehicleId: existingVehicle.id,
          vehicleData: vehicleData,
          hasClientId: 'client_id' in vehicleData,
          clientIdValue: vehicleData.client_id,
          hasQuoteId: 'quote_id' in vehicleData, 
          quoteIdValue: vehicleData.quote_id,
          allKeys: Object.keys(vehicleData),
          // Check for undefined values
          undefinedValues: Object.keys(vehicleData).filter(key => vehicleData[key] === undefined)
        });
        await axios.put(
          `${API_URL}/api/vehicles/${existingVehicle.id}`,
          vehicleData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        console.log('✅ Vehicle updated successfully');
      } else {
        // Vehicle doesn't exist in database -> CREATE NEW
        console.log('➕ Creating new vehicle (not found in database)');
        const response = await axios.post(
          `${API_URL}/api/vehicles`,
          vehicleData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        console.log('✅ New vehicle created with ID:', response.data.id || response.data.data?.id);
      }
      
      // Remove from map so we don't process it again
      existingVehicleMap.delete(vehicleKey);
      
    } catch (error) {
      console.error('❌ Error saving vehicle:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('🔍 Backend error details:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      errors.push(`Vehicle (${vehicle.registration_number || 'Unknown'}): ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  console.log("✅ VEHICLE SAVE PROCESS COMPLETE");
}

      // Save household contents
if (householdContents.length > 0) {
  for (const content of householdContents) {
    try {
      // Check if this is an existing household content
      if (content.id && !content.id.toString().startsWith('temp-')) {
        console.log('🔄 Updating existing household contents:', content.id);
        
        // Remove the temp ID and isTemp flag before sending to API
        const { id, isTemp, ...contentData } = content;
        
        // Prepare data for backend with correct field names
        const householdData = {
  coverType: contentData.coverType || contentData.cover_type,
  insuredValue: contentData.insuredValue ? parseFloat(contentData.insuredValue) : 0,
  addressLine1: contentData.addressLine1 || contentData.address_line_1 || '',
  addressLine2: contentData.addressLine2 || contentData.address_line_2 || '',
  suburb: contentData.suburb || '',
  cityTown: contentData.cityTown || contentData.city_town || '',
  postalCode: contentData.postalCode || contentData.postal_code || '',
  region: contentData.region || '',
  country: contentData.country || 'South Africa',
  propertyType: contentData.propertyType || contentData.property_type || '',
  residenceType: contentData.residenceType || contentData.residence_type || '',
  buildingAge: contentData.buildingAge || contentData.building_age || '',
  situatedType: contentData.situatedType || contentData.situated_type || '',
  yearsLivedOnProperty: contentData.yearsLivedOnProperty ? parseInt(contentData.yearsLivedOnProperty) : null,
  burglarBarType: contentData.burglarBarType || contentData.burglar_bar_type || '',
  dayTimeOccupantType: contentData.dayTimeOccupantType || contentData.day_time_occupant_type || '',
  conductsBusiness: contentData.conductsBusiness || false,
  hasSurveillanceCamera: contentData.hasSurveillanceCamera || false,
  hasExternalSlidingDoor: contentData.hasExternalSlidingDoor || false,
  hasSecurityGate: contentData.hasSecurityGate || false,
  inSecurityComplex: contentData.inSecurityComplex || false,
  hasAlarmRecords: contentData.hasAlarmRecords || false,
  hasNeighborhoodWatch: contentData.hasNeighborhoodWatch || false,
  alarmSystemType: contentData.alarmSystemType || contentData.alarm_system_type || '',
  perimeterType: Array.isArray(contentData.perimeterType) ? contentData.perimeterType.join(',') : (contentData.perimeterType || ''),
  hasAccidentalDamageCover: contentData.hasAccidentalDamageCover || false,
  accidentalDamageValue: contentData.accidentalDamageValue ? parseFloat(contentData.accidentalDamageValue) : 0,
  hasPowerSurgeCover: contentData.hasPowerSurgeCover || false,
  powerSurgeValue: contentData.powerSurgeValue ? parseFloat(contentData.powerSurgeValue) : 0,
  hasSwimmingPool: contentData.hasSwimmingPool || false,
  hasMotorizedGate: contentData.hasMotorizedGate || false,
  isPropertyUnoccupied: contentData.isPropertyUnoccupied || false,
  isCommune: contentData.isCommune || false,
  underConstruction: contentData.underConstruction || false,
  builtOnSteepSlope: contentData.builtOnSteepSlope || false,
  hasRetainingWalls: contentData.hasRetainingWalls || false,
  nearWaterBody: contentData.nearWaterBody || false,
  hasRisingDamp: contentData.hasRisingDamp || false,
  hasWallCracks: contentData.hasWallCracks || false,
  historyOfLandslip: contentData.historyOfLandslip || false,
  hasOutbuilding: contentData.hasOutbuilding || false,
  hasPerimeterFence: contentData.hasPerimeterFence || false,
  hasElectricFence: contentData.hasElectricFence || false,
  hasThatchRoof: contentData.hasThatchRoof || false,
  wallMaterials: Array.isArray(contentData.wallMaterials) ? contentData.wallMaterials.join(',') : (contentData.wallMaterials || ''),
  roofMaterials: Array.isArray(contentData.roofMaterials) ? contentData.roofMaterials.join(',') : (contentData.roofMaterials || ''),
  floorMaterials: Array.isArray(contentData.floorMaterials) ? contentData.floorMaterials.join(',') : (contentData.floorMaterials || ''),
  otherSurroundingArea: Array.isArray(contentData.otherSurroundingArea) ? contentData.otherSurroundingArea.join(',') : (contentData.otherSurroundingArea || ''),
  premium: contentData.premium ? parseFloat(contentData.premium) : 0,
  excess: contentData.excess ? parseFloat(contentData.excess) : 0,
  noClaimBonus: contentData.noClaimBonus || contentData.no_claim_bonus || '',
  quote_id: quoteId,
  client_id: parseInt(clientId!)
};
        
        // UPDATE existing
        const response = await axios.put(
          `${API_URL}/api/household-contents/${content.id}`,
          householdData,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        console.log('✅ Household contents updated successfully:', response.data);
      } else {
        // CREATE new
console.log('💾 Creating new household contents with quote ID:', quoteId);

// Remove the temp ID and isTemp flag before sending to API
const { id, isTemp, ...contentData } = content;

// Helper function to convert to number or 0
const toNumber = (value: any): number => {
  if (value === undefined || value === null || value === '') return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

// Helper function to convert to number or null
const toNumberOrNull = (value: any): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

// Prepare data for backend with correct field names
const householdData = {
  coverType: contentData.coverType || contentData.cover_type || '',
  insuredValue: toNumber(contentData.insuredValue || contentData.insured_value),
  addressLine1: contentData.addressLine1 || contentData.address_line_1 || '',
  addressLine2: contentData.addressLine2 || contentData.address_line_2 || '',
  suburb: contentData.suburb || '',
  cityTown: contentData.cityTown || contentData.city_town || '',
  postalCode: contentData.postalCode || contentData.postal_code || '',
  region: contentData.region || '',
  country: contentData.country || 'South Africa',
  propertyType: contentData.propertyType || contentData.property_type || '',
  residenceType: contentData.residenceType || contentData.residence_type || '',
  buildingAge: contentData.buildingAge || contentData.building_age || '',
  situatedType: contentData.situatedType || contentData.situated_type || '',
  yearsLivedOnProperty: toNumberOrNull(contentData.yearsLivedOnProperty || contentData.years_lived_on_property),
  burglarBarType: contentData.burglarBarType || contentData.burglar_bar_type || '',
  dayTimeOccupantType: contentData.dayTimeOccupantType || contentData.day_time_occupant_type || '',
  conductsBusiness: Boolean(contentData.conductsBusiness),
  hasSurveillanceCamera: Boolean(contentData.hasSurveillanceCamera),
  hasExternalSlidingDoor: Boolean(contentData.hasExternalSlidingDoor),
  hasSecurityGate: Boolean(contentData.hasSecurityGate),
  inSecurityComplex: Boolean(contentData.inSecurityComplex),
  hasAlarmRecords: Boolean(contentData.hasAlarmRecords),
  hasNeighborhoodWatch: Boolean(contentData.hasNeighborhoodWatch),
  alarmSystemType: contentData.alarmSystemType || contentData.alarm_system_type || '',
  perimeterType: Array.isArray(contentData.perimeterType) ? contentData.perimeterType.join(',') : (contentData.perimeterType || ''),
  hasAccidentalDamageCover: Boolean(contentData.hasAccidentalDamageCover),
  accidentalDamageValue: toNumber(contentData.accidentalDamageValue || contentData.accidental_damage_value),
  hasPowerSurgeCover: Boolean(contentData.hasPowerSurgeCover),
  powerSurgeValue: toNumber(contentData.powerSurgeValue || contentData.power_surge_value),
  hasSwimmingPool: Boolean(contentData.hasSwimmingPool),
  hasMotorizedGate: Boolean(contentData.hasMotorizedGate),
  isPropertyUnoccupied: Boolean(contentData.isPropertyUnoccupied),
  isCommune: Boolean(contentData.isCommune),
  underConstruction: Boolean(contentData.underConstruction),
  builtOnSteepSlope: Boolean(contentData.builtOnSteepSlope),
  hasRetainingWalls: Boolean(contentData.hasRetainingWalls),
  nearWaterBody: Boolean(contentData.nearWaterBody),
  hasRisingDamp: Boolean(contentData.hasRisingDamp),
  hasWallCracks: Boolean(contentData.hasWallCracks),
  historyOfLandslip: Boolean(contentData.historyOfLandslip),
  hasOutbuilding: Boolean(contentData.hasOutbuilding),
  hasPerimeterFence: Boolean(contentData.hasPerimeterFence),
  hasElectricFence: Boolean(contentData.hasElectricFence),
  hasThatchRoof: Boolean(contentData.hasThatchRoof),
  wallMaterials: Array.isArray(contentData.wallMaterials) ? contentData.wallMaterials.join(',') : (contentData.wallMaterials || ''),
  roofMaterials: Array.isArray(contentData.roofMaterials) ? contentData.roofMaterials.join(',') : (contentData.roofMaterials || ''),
  floorMaterials: Array.isArray(contentData.floorMaterials) ? contentData.floorMaterials.join(',') : (contentData.floorMaterials || ''),
  otherSurroundingArea: Array.isArray(contentData.otherSurroundingArea) ? contentData.otherSurroundingArea.join(',') : (contentData.otherSurroundingArea || ''),
  premium: toNumber(contentData.premium),
  excess: toNumber(contentData.excess),
  noClaimBonus: contentData.noClaimBonus || contentData.no_claim_bonus || '',
  quote_id: quoteId,
  client_id: parseInt(clientId!)
};

// Debug log to see what's being sent
console.log('🔍 Sending household data:', JSON.stringify(householdData, null, 2));
        
        const response = await axios.post(`${API_URL}/api/household-contents`, householdData, {
  headers: { 'Authorization': `Bearer ${token}` }
});
        console.log('✅ Household contents saved successfully with quote ID:', response.data);
      }
    } catch (error) {
      console.error('❌ Error saving household contents:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('🔍 Household contents error response:', error.response.data);
      }
      errors.push(`Household contents: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

      // ✅ Save trailers & caravans - ADD EDIT MODE CHECK
      if (trailersCaravans.length > 0) {
        console.log(`💾 DEBUG: Saving ${trailersCaravans.length} trailers/caravans for quote ${quoteId}`);
        
        // Use a regular for loop instead of for...of with entries()
        for (let i = 0; i < trailersCaravans.length; i++) {
          const trailer = trailersCaravans[i];
          const index = i; // Use i as index
          
          try {
            console.log(`🔍 DEBUG: Processing trailer ${index + 1}:`, {
              id: trailer.id,
              registrationNumber: trailer.registrationNumber,
              make: trailer.make,
              model: trailer.model,
              year: trailer.year,
              isTempId: trailer.id?.toString().startsWith('temp-'),
              rawData: trailer
            });
            
            // ⭐⭐ FIX: Use camelCase field names as backend expects
            const trailerData = {
              vehicleType: trailer.trailerType || trailer.vehicle_type || 'Trailer',
              registrationNumber: trailer.registrationNumber || trailer.registration_number || 'N/A',
              make: trailer.make || 'Unknown',
              model: trailer.model || 'Unknown',
              year: parseInt(trailer.year || '0') || 2020, // Must be number
              coverType: trailer.coverType || trailer.cover_type || 'Comprehensive',
              insuredValue: parseFloat(String(trailer.insuredValue)) || 0,
              client_id: parseInt(clientId!),
              useRetailValue: Boolean(trailer.useRetailValue || trailer.use_retail_value || false),
              premium: parseFloat(String(trailer.premium)) || 0,
              excess: parseFloat(String(trailer.excess)) || 0,
              
              // ⭐⭐ CRITICAL FIX: Clean up noClaimsBonus format
              noClaimsBonus: (trailer.noClaimsBonus || trailer.no_claims_bonus || 'None')
                .replace(/\s+/g, ' ')  // Remove extra spaces
                .trim(),
                
              quote_id: quoteId || null
            };

            console.log(`📤 DEBUG: Sending trailer ${index + 1} to API:`, JSON.stringify(trailerData, null, 2));

            // ⭐⭐ ADD: Validate before sending
            const trailerValidationErrors = [];
            if (!trailerData.vehicleType) trailerValidationErrors.push('vehicleType');
            if (!trailerData.registrationNumber || trailerData.registrationNumber === 'N/A') trailerValidationErrors.push('registrationNumber');
            if (!trailerData.make || trailerData.make === 'Unknown') trailerValidationErrors.push('make');
            if (!trailerData.model || trailerData.model === 'Unknown') trailerValidationErrors.push('model');
            if (!trailerData.year || trailerData.year === 0) trailerValidationErrors.push('year');
            if (!trailerData.coverType) trailerValidationErrors.push('coverType');
            if (!trailerData.insuredValue || trailerData.insuredValue === 0) trailerValidationErrors.push('insuredValue');
            if (!trailerData.client_id) trailerValidationErrors.push('client_id');
            if (trailerData.premium === undefined) trailerValidationErrors.push('premium');
            if (trailerData.excess === undefined) trailerValidationErrors.push('excess');
            if (!trailerData.noClaimsBonus) trailerValidationErrors.push('noClaimsBonus');

            if (trailerValidationErrors.length > 0) {
              console.error(`❌ Trailer ${index + 1}: Missing fields:`, trailerValidationErrors);
              errors.push(`Trailer/Caravan ${index + 1}: Missing fields: ${trailerValidationErrors.join(', ')}`);
              continue; // Skip to next trailer
            }

            // Validate all required fields are present
            // Validate all required fields are present (allow 0 for numeric fields)
const requiredFields = [
  'vehicleType',
  'registrationNumber', 
  'make',
  'model',
  'year',
  'coverType',
  'client_id',
  'noClaimsBonus'
];

const missingFields = requiredFields.filter(field => {
  const value = trailerData[field as keyof typeof trailerData];
  return value === undefined || value === null || value === '';
});

// For numeric fields, check they exist (0 is valid)
if (trailerData.insuredValue === undefined || trailerData.insuredValue === null) {
  missingFields.push('insuredValue');
}
if (trailerData.premium === undefined || trailerData.premium === null) {
  missingFields.push('premium');
}
if (trailerData.excess === undefined || trailerData.excess === null) {
  missingFields.push('excess');
}

if (missingFields.length > 0) {
  console.error(`❌ Trailer ${index + 1}: Missing fields:`, missingFields);
  errors.push(`Trailer/Caravan ${index + 1}: Missing fields: ${missingFields.join(', ')}`);
  continue; // Skip to next trailer
}

            // Check if this is a temporary ID (created in frontend)
            const isTempId = trailer.id && trailer.id.toString().startsWith('temp-');
            
            if (!isTempId && trailer.id) {
              // UPDATE existing trailer
              console.log(`🔄 DEBUG: Updating existing trailer ID: ${trailer.id}`);
              const response = await axios.put(
                `${API_URL}/api/trailer-caravans/${trailer.id}`,
                { quote_id: quoteId }, // PUT only needs quote_id
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              console.log(`✅ DEBUG: Trailer updated:`, response.data);
            } else {
              // CREATE new trailer
              console.log(`➕ DEBUG: Creating new trailer`);
              const response = await axios.post(
                `${API_URL}/api/trailer-caravans`, // ⭐ Correct endpoint
                trailerData,
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              console.log(`✅ DEBUG: Trailer created:`, response.data);
            }
            
          } catch (error) {
            console.error(`❌ DEBUG: Error saving trailer ${index + 1}:`, error);
            if (axios.isAxiosError(error)) {
              console.error('🔍 DEBUG: Axios error details:', {
                url: error.config?.url,
                method: error.config?.method,
                data: error.config?.data,
                status: error.response?.status,
                response: error.response?.data
              });
            }
            errors.push(`Trailer/Caravan ${index + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      } else {
        console.log('ℹ️ DEBUG: No trailers/caravans to save');
      }

      // Save value added products
      if (valueAddedProducts.length > 0) {
  console.log(`💾 Saving ${valueAddedProducts.length} value-added products for quote ${quoteId}`);
  
  for (const product of valueAddedProducts) {
    try {
      console.log('🔍 Processing value-added product:', product);
      
      // Check if this is an existing product (has real ID)
      const isTempId = product.id?.toString().startsWith('temp-');
      
      // Map product type - keep original values (no transformation)
      const mapProductType = (type: string): string => {
        // Valid product types from dropdown
        const validTypes = [
          'GAP Cover', 'Credit Shortfall', 'Car Hire', 'Excess Waiver',
          'Key Cover', 'Tyre and Rim Cover', 'Mobile Device Cover',
          'Laptop Cover', 'Furniture Cover', 'Appliance Cover', 'Other'
        ];
        
        // If the type is valid, return it as-is
        if (validTypes.includes(type)) {
          return type;
        }
        
        // Fallback to 'Other' for unknown types
        console.warn(`Unknown product type: "${type}", defaulting to "Other"`);
        return 'Other';
      };

const valueAddedData = {
  productType: mapProductType(product.productType || product.product_type),
  description: product.description,
  value: parseFloat(product.value || product.insuredValue || '0'),
  client_id: parseInt(clientId!),
  premium: parseFloat(product.premium || '0'),
  quote_id: quoteId
};

      console.log('📤 Sending value-added data to API:', valueAddedData);

      if (!isTempId && product.id) {
        // UPDATE existing product
        console.log('🔄 Updating existing value added product:', product.id);
        
        const response = await axios.put(`${API_URL}/api/value-added/${product.id}`, valueAddedData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ Value added product updated successfully:', response.data);
      } else {
        // CREATE new product (from temp ID)
        console.log('➕ Creating new value added product with quote ID:', quoteId);
        
        const response = await axios.post(`${API_URL}/api/value-added`, valueAddedData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ Value added product saved successfully:', response.data);
      }
    } catch (error) {
      console.error('❌ Error saving value added product:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('🔍 Backend error response:', error.response.data);
        console.error('🔍 Backend error status:', error.response.status);
      }
      errors.push(`Value added product: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

      // Save risk items
if (riskItems.length > 0) {
  for (const riskItem of riskItems) {
    try {
      console.log('💾 Processing risk item:', riskItem.id);
      
      // Ensure all required fields have values
      const itemType = riskItem.itemType || riskItem.item_type || 'Other';
      const itemName = riskItem.itemName || riskItem.item_name || 'Unnamed Item';
      const description = riskItem.description || 'No description';
      const coverType = riskItem.coverType || riskItem.cover_type || 'Comprehensive';
      const noClaimsBonusValue = riskItem.noClaimsBonus || riskItem.no_claims_bonus || 'None';
      const backendNoClaimsBonus = mapNoClaimsBonusForBackend(noClaimsBonusValue);
      const insuredValue = parseFloat(riskItem.insuredValue || riskItem.insured_value || 0);
      const premium = parseFloat(riskItem.premium || 0);
      const excess = parseFloat(riskItem.excess || 0);
      
      // Map item type to allowed backend values
      const mapItemTypeForBackend = (frontendType: string): string => {
        const mapping: { [key: string]: string } = {
          'Jewelry': 'Jewellery',
          'Watches': 'Watch-Smart Mobile Device',
          'Watch': 'Watch-Smart Mobile Device',
          'Cameras': 'Camera / Equipment',
          'Camera': 'Camera / Equipment',
          'Laptops': 'Laptops',
          'Laptop': 'Laptops',
          'Tablets': 'Tablet-Mobile Device',
          'Tablet': 'Tablet-Mobile Device',
          'Mobile Phones': 'Mobile Phone',
          'Mobile Device': 'Mobile Phone',
          'Bicycles': 'Pedal Cycles',
          'Musical Instruments': 'Musical Instruments',
          'Artwork': 'Artwork and Antiques',
          'Antiques': 'Artwork and Antiques',
          'Collectibles': 'Collectables',
          'Sports Equipment': 'Sport Equipment',
          'Tools': 'Other',
          'Electronics': 'Other',
          'Furniture': 'Other',
          'Tracking Device': 'Tracking Device',
          'Watch-Smart Mobile Device': 'Watch-Smart Mobile Device',
          'Unspecified': 'Unspecified',
          'Other': 'Other'
        };
        
        const mapped = mapping[frontendType] || frontendType;
        const validItemTypes = [
          'Unspecified', 'Other', 'Artwork and Antiques', 'Blanket Cover', 'Camera / Equipment',
          'Car Radio', 'Camping Equipment', 'Caravan Content', 'Mobile Phone', 'Mobile Phone Accessories',
          'Collectables', 'Computer', 'Contact Lenses', 'Diving Equipment', 'Drones', 'Firearm',
          'Fishing Equipment', 'Golf Cart', 'Golf Equipment', 'Goods In Vault', 'GPS', 'Hearing Aid',
          'Household Goods In Transit', 'Jewellery', 'Keys, Locks and Remote Control Units', 'Laptops',
          'Musical Instruments', 'Musical Instruments Professional', 'Pedal Cycles', 'Personal Documents',
          'Portable Radio', 'Portable TV', 'Prescription Glasses', 'Quad Bike', 'Sport Equipment',
          'Sunglasses', 'Tablet-Mobile Device', 'Tracking Device', 'Watch-Smart Mobile Device'
        ];
        
        if (!validItemTypes.includes(mapped)) {
          console.warn(`⚠️ Item type "${mapped}" not in allowed values, defaulting to "Other"`);
          return 'Other';
        }
        
        return mapped;
      };
      
      const riskItemData = {
        item_type: mapItemTypeForBackend(itemType),
        description: description,
        item_name: itemName,
        serial_number: riskItem.serialNumber || riskItem.serial_number || '',
        cover_type: coverType,
        no_claims_bonus: backendNoClaimsBonus,
        insured_value: insuredValue,
        premium: premium,
        excess: excess,
        client_id: parseInt(clientId!),
        quote_id: quoteId
      };
      
      console.log('📤 Sending risk item data (SNAKE_CASE):', riskItemData);
      
      // Validation log
      const validation = {
        has_item_type: !!riskItemData.item_type,
        has_description: !!riskItemData.description,
        has_item_name: !!riskItemData.item_name,
        has_insured_value: riskItemData.insured_value !== undefined,
        has_client_id: !!riskItemData.client_id,
        has_quote_id: !!riskItemData.quote_id,
        has_premium: riskItemData.premium !== undefined,
        has_cover_type: !!riskItemData.cover_type,
        has_no_claims_bonus: !!riskItemData.no_claims_bonus,
        has_excess: riskItemData.excess !== undefined
      };
      
      console.log('🔍 Validation check:', validation);
      
      // Check if all required fields are present
      const missingFields = [];
      if (!riskItemData.item_type) missingFields.push('item_type');
      if (!riskItemData.description) missingFields.push('description');
      if (!riskItemData.item_name) missingFields.push('item_name');
      if (riskItemData.insured_value === undefined) missingFields.push('insured_value');
      if (!riskItemData.client_id) missingFields.push('client_id');
      if (!riskItemData.quote_id) missingFields.push('quote_id');
      if (riskItemData.premium === undefined) missingFields.push('premium');
      if (!riskItemData.cover_type) missingFields.push('cover_type');
      if (!riskItemData.no_claims_bonus) missingFields.push('no_claims_bonus');
      if (riskItemData.excess === undefined) missingFields.push('excess');
      
      if (missingFields.length > 0) {
        console.error(`❌ Risk item missing required fields:`, missingFields);
        errors.push(`Risk item: Missing fields: ${missingFields.join(', ')}`);
        continue;
      }
      
      if (riskItem.id && !riskItem.id.toString().startsWith('temp-')) {
        console.log('🔄 Updating existing risk item');
        const response = await axios.put(`${API_URL}/api/risk-items/${riskItem.id}`, riskItemData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ Updated:', response.data);
      } else {
        console.log('➕ Creating new risk item');
        const response = await axios.post(`${API_URL}/api/risk-items`, riskItemData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ Created:', response.data);
      }
    } catch (error) {
      console.error('❌ Error:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('🔍 Backend error:', error.response.data);
        console.error('🔍 Error status:', error.response.status);
      }
      errors.push(`Risk item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

      // Save water crafts
      if (waterCrafts.length > 0) {
        console.log(`🚤 Saving ${waterCrafts.length} watercrafts for quote ${quoteId}`);
        for (const waterCraft of waterCrafts) {
          try {
            // Convert camelCase to snake_case for backend - FIX registered_owner
      const waterCraftData = {
        year: waterCraft.year ? Number(waterCraft.year) : null,
        craft_type: waterCraft.craftType || null,
        hull_make: waterCraft.hullMake || null,
        hull_length: waterCraft.hullLength ? Number(waterCraft.hullLength) : null,
        year_model: waterCraft.yearModel ? Number(waterCraft.yearModel) : null,
        body_paint: waterCraft.bodyPaint || null,
        outboard_motor_make: waterCraft.outboardMotorMake || null,
        outboard_engine_number: waterCraft.outboardEngineNumber || null,
        horse_power: waterCraft.horsePower ? Number(waterCraft.horsePower) : null,
        max_speed: waterCraft.maxSpeed ? Number(waterCraft.maxSpeed) : null,
        vessel_name: waterCraft.vesselName || null,
        vessel_storage: waterCraft.vesselStorage || null,
        vessel_usage: waterCraft.vesselUsage || null,
        amateur_built: waterCraft.amateurBuilt ?? false,
        // FIX: Strip 'poi-' prefix if present
        registered_owner: waterCraft.registeredOwner ? String(waterCraft.registeredOwner).replace('poi-', '') : null,
        finance_house: waterCraft.financeHouse || null,
        finance_branch: waterCraft.financeBranch || null,
        hull_value: waterCraft.hullValue ? Number(waterCraft.hullValue) : 0,
        outboard_value: waterCraft.outboardValue ? Number(waterCraft.outboardValue) : 0,
        sails_value: waterCraft.sailsValue ? Number(waterCraft.sailsValue) : 0,
        total_sum_insured: waterCraft.totalSumInsured ? Number(waterCraft.totalSumInsured) : 0,
        total_monthly_premium: waterCraft.totalMonthlyPremium ? Number(waterCraft.totalMonthlyPremium) : 0,
        trailer_make: waterCraft.trailerMake || null,
        trailer_model: waterCraft.trailerModel || null,
        trailer_registration: waterCraft.trailerRegistration || null,
        trailer_value: waterCraft.trailerValue ? Number(waterCraft.trailerValue) : 0,
        trailer_premium: waterCraft.trailerPremium ? Number(waterCraft.trailerPremium) : 0,
        client_id: parseInt(clientId!),
        quote_id: quoteId
      };

      console.log('📤 Sending watercraft data (snake_case):', waterCraftData);

      if (waterCraft.id && !waterCraft.id.toString().startsWith('temp-')) {
        console.log('🔄 Updating existing water craft:', waterCraft.id);
        
        const response = await axios.put(`${API_URL}/api/water-craft/${waterCraft.id}`, 
          waterCraftData, 
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        console.log('✅ Water craft updated successfully:', response.data);
      } else {
        console.log('💾 Creating new water craft with quote ID:', quoteId);
        
        const response = await axios.post(`${API_URL}/api/water-craft`, 
          waterCraftData, 
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        console.log('✅ Water craft saved successfully:', response.data);
      }
    } catch (error) {
      console.error('❌ Error saving water craft:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('🔍 Water craft error response:', error.response.data);
        console.error('🔍 Error status:', error.response.status);
      }
      errors.push(`Water craft: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

      // ✅ Save previous claims - ADD EDIT MODE CHECK
      if (previousClaims.length > 0) {
        for (const claim of previousClaims) {
          try {
            // Check if this is an existing claim
            if (claim.id && !claim.id.toString().startsWith('temp-')) {
              console.log('🔄 Updating existing claim:', claim.id);
              
              // Parse claim value properly
              let claimValue = null;
              if (claim.claimValue && claim.claimValue.trim() !== '') {
                const parsedValue = parseFloat(claim.claimValue);
                if (!isNaN(parsedValue)) {
                  claimValue = parsedValue;
                }
              }
              
              const claimData = {
                client_id: parseInt(clientId!),
                description: claim.description,
                policy_number: claim.policyNumber,
                previous_insurer: claim.previousInsurer || null,
                claim_date: claim.claimDate || new Date().toISOString().split('T')[0],
                claim_value: claimValue,
                quote_id: quoteId
              };

              console.log('📤 Updating claim data:', claimData);
              
              const response = await axios.put(`${API_URL}/api/clients-personal-claims/${claim.id}`, claimData, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              console.log('✅ Claim updated successfully:', response.data);
            } else {
              // CREATE new
              console.log('💾 Creating new claim with quote ID:', quoteId);
              console.log('🔍 Raw claim data:', claim);
              
              // Parse claim value properly
              let claimValue = null;
              if (claim.claimValue && claim.claimValue.trim() !== '') {
                const parsedValue = parseFloat(claim.claimValue);
                if (!isNaN(parsedValue)) {
                  claimValue = parsedValue;
                }
              }
              
              const claimData = {
                client_id: parseInt(clientId!),
                description: claim.description,
                policy_number: claim.policyNumber,
                previous_insurer: claim.previousInsurer || null,
                claim_date: claim.claimDate || new Date().toISOString().split('T')[0],
                claim_value: claimValue,
                quote_id: quoteId
              };

              console.log('📤 Sending claim data:', claimData);
              
              const response = await axios.post(`${API_URL}/api/clients-personal-claims`, claimData, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              console.log('✅ Claim saved successfully:', response.data);
            }
          } catch (error) {
            console.error('❌ Error saving claim:', error);
            if (axios.isAxiosError(error) && error.response) {
              console.error('🔍 Claim error response:', error.response.data);
            }
            errors.push(`Claim: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      }

      // ✅ ADD THIS: Save previous insurances
if (previousInsurances.length > 0) {
  console.log(`💾 Saving ${previousInsurances.length} previous insurances for quote ${quoteId}`);
  
  for (const insurance of previousInsurances) {
    try {
      // Check if this is an existing insurance
      if (insurance.id && !insurance.id.toString().startsWith('temp-')) {
        console.log('🔄 Updating existing previous insurance:', insurance.id);
        
        const insuranceData = {
          description: insurance.description,
          policy_number: insurance.policyNumber,
          insurer: insurance.insurer,
          start_date: insurance.startDate,
          end_date: insurance.endDate,
          client_id: parseInt(clientId!),
          quote_id: quoteId
        };
        
        const response = await axios.put(
          `${API_URL}/api/previous-insurances/${insurance.id}`,
          insuranceData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        console.log('✅ Previous insurance updated successfully:', response.data);
      } else {
        // CREATE new
        console.log('💾 Creating new previous insurance with quote ID:', quoteId);
        
        const insuranceData = {
          description: insurance.description,
          policy_number: insurance.policyNumber,
          insurer: insurance.insurer,
          start_date: insurance.startDate,
          end_date: insurance.endDate,
          client_id: parseInt(clientId!),
          quote_id: quoteId
        };
        
        const response = await axios.post(
          `${API_URL}/api/previous-insurances`,
          insuranceData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        console.log('✅ Previous insurance saved successfully:', response.data);
      }
    } catch (error) {
      console.error('❌ Error saving previous insurance:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('🔍 Previous insurance error response:', error.response.data);
      }
      errors.push(`Previous insurance: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

      // ✅ Update notes with quote ID - ADD EDIT MODE CHECK
      if (notes.length > 0) {
        for (const note of notes) {
          try {
            // Check if this is an existing note
            if (note.id && !note.id.toString().startsWith('temp-')) {
              console.log('🔄 Updating existing note:', note.id);
              
              const noteData = {
                note_content: note.content,
                quote_id: quoteId,
                client_id: parseInt(clientId!)
              };
              
              const response = await axios.put(`${API_URL}/api/notes/${note.id}`, noteData, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              console.log('✅ Note updated successfully:', response.data);
            } else {
              // CREATE new
              const noteData = {
                note_content: note.content,
                quote_id: quoteId,
                client_id: parseInt(clientId!)
              };
              
              const response = await axios.post(`${API_URL}/api/notes`, noteData, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              console.log('✅ Note saved successfully with quote ID');
            }
          } catch (error) {
            console.error('❌ Error saving note:', error);
            errors.push(`Note: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      }

      // ✅ Upload files (both already-uploaded temp files AND buffered File objects)
      if (uploadedFiles.length > 0) {
        console.log(`🔗 Uploading ${uploadedFiles.length} file(s) to quote ${quoteId}`);

        const alreadyUploaded = uploadedFiles.filter((f: any) => f.file_id || f.alreadyUploaded);
        const bufferedFiles   = uploadedFiles.filter((f: any) => f._fileObject && f._fileObject instanceof File);

        // 1. Associate files that were already uploaded to the server
        if (alreadyUploaded.length > 0) {
          try {
            const uploadResult = await uploadQuoteFiles(quoteId, alreadyUploaded, clientId!);
            if (uploadResult.errors.length > 0) {
              console.warn(`⚠️ ${uploadResult.errors.length} file association(s) failed`);
              errors.push(...uploadResult.errors);
            }
            console.log(`✅ ${uploadResult.successfulUploads.length} file(s) associated`);
          } catch (err) {
            console.error('❌ Error associating temp files:', err);
            errors.push(`File association: ${err instanceof Error ? err.message : 'Unknown error'}`);
          }
        }

        // 2. Upload buffered File objects directly to the quote now that we have a quoteId
        if (bufferedFiles.length > 0) {
          const formData = new FormData();
          bufferedFiles.forEach((f: any) => formData.append('files', f._fileObject));
          formData.append('quoteId', String(quoteId));

          const combinedDescription = bufferedFiles
            .map((f: any) => f.description || f.file_name)
            .join(', ');
          formData.append('description', combinedDescription);

          try {
            await axios.post(
              `${API_URL}/api/quotes/upload-files`,
              formData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
            console.log(`✅ ${bufferedFiles.length} buffered file(s) uploaded to quote`);
          } catch (err: any) {
            console.error('❌ Error uploading buffered files:', err);
            errors.push(`File upload: ${err.response?.data?.message || err.message}`);
          }
        }

        // Clear the uploaded files state after successful upload
        setUploadedFiles([]);
        localStorage.removeItem(`client_${clientId}_files_draft`);
      }

      console.log('✅ All products saved to database successfully');
      
      // If there were errors, log them but don't fail the entire process
      if (errors.length > 0) {
        console.warn('⚠️ Some products failed to save:', errors);
        // You can choose to throw an error here or just log warnings
        // throw new Error(`Failed to save ${errors.length} products: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('❌ Error in saveAllProductsToDatabase:', error);
      throw new Error('Failed to save some products to database');
    }
  };

  // update existing product
const updateProductInDatabase = async (productType: string, productId: number, data: any, quoteId: number) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  const endpoints: { [key: string]: string } = {
    property: `${API_URL}/api/properties/${productId}`,
    vehicle: `${API_URL}/api/vehicles/${productId}`,
    household: `${API_URL}/api/household-contents/${productId}`,
    trailer: `${API_URL}/api/trailer-caravans/${productId}`,
    value_added: `${API_URL}/api/value-added/${productId}`,
    risk_item: `${API_URL}/api/risk-items/${productId}`,
    water_craft: `${API_URL}/api/water-craft/${productId}`,
    claim: `${API_URL}/api/clients-personal-claims/${productId}`,
    note: `${API_URL}/api/notes/${productId}`,
  };

  const endpoint = endpoints[productType];
  if (!endpoint) {
    throw new Error(`Unknown product type: ${productType}`);
  }

  const response = await axios.put(endpoint, { ...data, quote_id: quoteId }, {
    headers: { Authorization: `Bearer ${token}` }
  });

  return response.data;
};

const handleDeclarationCheckboxChange = (questionId: number, checked: boolean) => {
  setSelectedDeclarations(prev => ({
    ...prev,
    [questionId]: checked
  }));
};

    // Handler for save draft
    const handleSaveDraft = () => {
      handleSaveQuote(true);
    };

    // Handler for submit quote
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      const quoteData = {
        underwriter_id: selectedUnderwriters, // <-- send the selected underwriter
      };

      try {
        await axios.post(`${API_URL}/api/quotes`, quoteData);
        alert("Quote created successfully!");
      } catch (err) {
        console.error("Error creating quote:", err);
        alert("Failed to create quote.");
      }
    };


  const handleQuoteReferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  setQuoteReference(event.target.value);
};

  // Handler for product dropdown changes
  const handleProductChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  const productId = event.target.value;
  setSelectedProduct(productId);

  localStorage.setItem('temp_selected_administrators', JSON.stringify(selectedAdministrators));
  localStorage.setItem('temp_selected_underwriters', JSON.stringify(selectedUnderwriters));
  console.log('💾 Saved administrators before navigation:', selectedAdministrators);
  
  // Map product ID to type and name for logging
  const productMap: { [key: string]: { type: string; name: string } } = {
    '1': { type: 'property', name: 'Property' },
    '2': { type: 'household', name: 'Household Contents' },
    '3': { type: 'vehicle', name: 'Vehicle' },
    '4': { type: 'trailer', name: 'Trailer & Caravan' },
    '5': { type: 'risk_item', name: 'Risk Item' },
    '6': { type: 'value_added', name: 'Value Added' },
    '14': { type: 'water_craft', name: 'Water Craft' },
  };
  
  // Log product addition before navigation - pass currentQuoteId (can be null)
  const productInfo = productMap[productId];
  if (productInfo) {
    logProductAddition(currentQuoteId, productInfo.type, productInfo.name);
  }
  
  // First, close ALL modals
  setIsVehicleModalVisible(false);
  setIsHouseholdContentsModalVisible(false);
  setIsTrailerCaravanModalVisible(false);
  setIsValueAddedModalVisible(false);
  setIsRiskItemsModalVisible(false);
  setIsWaterCraftModalVisible(false);
  
  // Small delay to ensure state updates are processed
  setTimeout(() => {
    // Then open the appropriate modal
    if (productId === '1') {
      navigate(`/add-property/${currentQuoteId}?clientId=${clientId}`);
    } else if (productId === '2') {
      navigate(`/add-household-contents/${currentQuoteId}?clientId=${clientId}`);
    } else if (productId === '3') {
      navigate(`/add-vehicle/${currentQuoteId}?clientId=${clientId}`);
    } else if (productId === '4') {
      navigate(`/add-trailer-caravan/${currentQuoteId}?clientId=${clientId}`);
    } else if (productId === '5') {
      navigate(`/add-risk-item/${currentQuoteId}?clientId=${clientId}`);
    } else if (productId === '6') {
      navigate(`/add-value-added/${currentQuoteId}?clientId=${clientId}`);
    } else if (productId === '14') {
      navigate(`/add-water-craft/${currentQuoteId}?clientId=${clientId}`);
    }
  }, 50);
  
  // Reset the dropdown selection after opening modal
  setTimeout(() => {
    setSelectedProduct('');
  }, 100);
};

  const handleCloseVehicleModal = () => {
    setIsVehicleModalVisible(false);
  };

 // Handler to close Household Contents modal
  const handleCloseHouseholdContentsModal = () => {
    setIsHouseholdContentsModalVisible(false);
  };

  // handle saving household contents
  const handleSaveHouseholdContents = (contentsData: any) => {
  const newContent = {
    ...contentsData,
    id: `temp-household-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
  };
  setHouseholdContents(prev => [...prev, newContent]);
  console.log('✅ Household contents added to local state:', newContent);
  setSaveStatus('success');
  setSaveMessage('Household contents added — saved when quote is created');
  handleCloseHouseholdContentsModal();
};

   const handleCloseValueAddedModal = () => {
    setIsValueAddedModalVisible(false);
  };

  const handleSaveValueAdded = (valueAddedData: any) => {
  console.log('🔄 handleSaveValueAdded called with:', valueAddedData);
  
  // Generate a unique ID
  const tempId = `temp-valueadded-${Date.now()}-${Math.random().toString(36).slice(2,10)}`;
  
  const productWithId = {
    ...valueAddedData,
    id: tempId,
    tempId: tempId, // Add tempId as separate property for easier tracking
  };

  console.log('📦 Product with generated ID:', productWithId);

  // STRICTER duplicate check
  const isDuplicate = valueAddedProducts.some(item => {
    // Check by ID first
    if (item.id === productWithId.id) return true;
    
    // Check by content
    return item.productType === productWithId.productType && 
           item.description === productWithId.description &&
           item.value === productWithId.value;
  });
  
  if (isDuplicate) {
    console.warn('⚠️ Duplicate value added product skipped - exact match found');
    setSaveStatus('warning');
    setSaveMessage('This value added product appears to already be added!');
    return;
  }

  // ADD TO LOCAL STATE ONLY - NO DATABASE SAVE
  setValueAddedProducts(prev => {
    console.log('📝 Adding product to state. Previous count:', prev.length);
    const newState = [...prev, productWithId];
    console.log('✅ New state count:', newState.length);
    return newState;
  });

  setSaveStatus('success');
  setSaveMessage('Value Added product added — will be saved when quote is created');
  handleCloseValueAddedModal();
};

  const handleCloseRiskItemsModal = () => {
  setIsRiskItemsModalVisible(false);
};

const handleSaveRiskItems = (riskItemData: any) => {
  console.log('🔄 RAW risk item data from modal:', riskItemData);
  
  // Map noClaimsBonus for frontend display (keep the original format for UI)
  const mapBackendToFrontendNoClaimsBonus = (backendValue: string): string => {
    const mapping: { [key: string]: string } = {
      "None": "None",
      "1 Year": "12 Months (1 Year)",
      "2 Year": "24 Months (2 Year)",
      "3 Year": "36 Months (3 Year)",
      "4 Year": "48 Months (4 Year)",
      "5 Year Plus": "60 Months (5 Year)",
    };
    return mapping[backendValue] || "None";
  };

  // Handle different possible response structures from the API
  // The backend returns snake_case, so we need to map to camelCase
  const itemType = 
    riskItemData.itemType || 
    riskItemData.item_type || 
    (riskItemData.data && riskItemData.data.itemType) || 
    (riskItemData.data && riskItemData.data.item_type) || 
    '';
  
  const itemName = 
    riskItemData.itemName || 
    riskItemData.item_name || 
    (riskItemData.data && riskItemData.data.itemName) || 
    (riskItemData.data && riskItemData.data.item_name) || 
    '';
  
  const serialNumber = 
    riskItemData.serialNumber || 
    riskItemData.serial_number || 
    (riskItemData.data && riskItemData.data.serialNumber) || 
    (riskItemData.data && riskItemData.data.serial_number) || 
    '';
  
  const coverType = 
    riskItemData.coverType || 
    riskItemData.cover_type || 
    (riskItemData.data && riskItemData.data.coverType) || 
    (riskItemData.data && riskItemData.data.cover_type) || 
    '';
  
  // Map noClaimsBonus for backend
  const backendNoClaimsBonus = mapNoClaimsBonusForBackend(riskItemData.noClaimsBonus || 'None');
  
  const noClaimsBonus = mapBackendToFrontendNoClaimsBonus(backendNoClaimsBonus);
  
  const insuredValue = 
    riskItemData.insuredValue || 
    riskItemData.insured_value || 
    (riskItemData.data && riskItemData.data.insuredValue) || 
    (riskItemData.data && riskItemData.data.insured_value) || 
    0;
  
  const premium = 
    riskItemData.premium || 
    (riskItemData.data && riskItemData.data.premium) || 
    0;
  
  const excess = 
    riskItemData.excess || 
    (riskItemData.data && riskItemData.data.excess) || 
    0;
  
  // Create properly formatted risk item for the card
  const formattedRiskItem = {
    id: `temp-riskitem-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
    itemType: riskItemData.itemType || riskItemData.item_type || '',
    description: riskItemData.description || riskItemData.data?.description || '',
    itemName: riskItemData.itemName || riskItemData.item_name || '',
    serialNumber: riskItemData.serialNumber || riskItemData.serial_number || '',
    coverType: riskItemData.coverType || riskItemData.cover_type || '',
    noClaimsBonus: riskItemData.noClaimsBonus || 'None', // Frontend display format
    no_claims_bonus: backendNoClaimsBonus, // Store backend mapped value
    insuredValue: riskItemData.insuredValue || riskItemData.insured_value || 0,
    premium: riskItemData.premium || 0,
    excess: riskItemData.excess || 0,
    isEditing: false
  };
  
  console.log('✅ FINAL formatted risk item:', formattedRiskItem);
  console.log('🔍 No Claims Bonus fields:', {
    frontend_display: formattedRiskItem.noClaimsBonus,
    backend_value: formattedRiskItem.no_claims_bonus
  });
  
  setRiskItems(prev => [...prev, formattedRiskItem]);
  setSaveStatus('success');
  setSaveMessage('Risk item added — saved when quote is created');
  handleCloseRiskItemsModal();
};

  // handler for multiple administrators
  const handleAdministratorsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(event.target.selectedOptions, option => option.value);
    setSelectedAdministrators(selectedOptions);
  };

  // handler for multiple underwriters
  const handleUnderwritersChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(event.target.selectedOptions, option => option.value);
    setSelectedUnderwriters(selectedOptions);
  };

  // New handler for the duration dropdown
  const handleDurationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDuration(event.target.value);
  };

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    //console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    //console.log(`${type} image loaded successfully`);
  };

  // Function to load existing products for editing
  async function loadExistingProducts(quoteId: string, clientId: string) {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found');
      return;
    }

    console.log('📦 Loading existing products for quote:', quoteId);
    
    // Load Properties
    try {
      const response = await axios.get(`${API_URL}/api/properties?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      setProperties(items);
      console.log(`✅ Loaded ${items.length} properties for editing`);
    } catch (error) {
      console.error('❌ Error loading properties:', error);
      setProperties([]);
    }

    // Load Vehicles
    try {
      const response = await axios.get(`${API_URL}/api/vehicles?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      setVehicles(items);
      console.log(`✅ Loaded ${items.length} vehicles for editing`);
    } catch (error) {
      console.error('❌ Error loading vehicles:', error);
      setVehicles([]);
    }

    // Load Household Contents
    try {
      const response = await axios.get(`${API_URL}/api/household-contents?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      setHouseholdContents(items);
      console.log(`✅ Loaded ${items.length} household contents for editing`);
    } catch (error) {
      console.error('❌ Error loading household contents:', error);
      setHouseholdContents([]);
    }

    // Load Trailers/Caravans
    try {
      const response = await axios.get(`${API_URL}/api/trailer-caravans?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      setTrailersCaravans(items);
      console.log(`✅ Loaded ${items.length} trailers/caravans for editing`);
    } catch (error) {
      console.error('❌ Error loading trailers/caravans:', error);
      setTrailersCaravans([]);
    }

    // Load Value Added Products
    try {
      const response = await axios.get(`${API_URL}/api/value-added?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      setValueAddedProducts(items);
      console.log(`✅ Loaded ${items.length} value added products for editing`);
    } catch (error) {
      console.error('❌ Error loading value added products:', error);
      setValueAddedProducts([]);
    }

    // Load Risk Items
try {
  const response = await axios.get(`${API_URL}/api/risk-items?quote_id=${quoteId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = response.data.data || response.data;
  const items = Array.isArray(data) ? data : [];
  
  // Map the risk items with all required fields
  const formattedRiskItems = items.map((item: any) => ({
    id: item.id,
    itemType: item.item_type || '',
    description: item.description || '',
    itemName: item.item_name || '',
    serialNumber: item.serial_number || '',
    coverType: item.cover_type || 'Comprehensive',
    noClaimsBonus: item.no_claims_bonus || 'None',
    insuredValue: item.insured_value || 0,
    premium: item.premium || 0,
    excess: item.excess || 0,
    isEditing: false
  }));
  
  setRiskItems(formattedRiskItems);
  console.log(`✅ Loaded ${formattedRiskItems.length} risk items for editing`);
} catch (error) {
  console.error('❌ Error loading risk items:', error);
  setRiskItems([]);
}

    // Load Water Crafts
    try {
      const response = await axios.get(`${API_URL}/api/water-craft?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      setWaterCrafts(items);
      console.log(`✅ Loaded ${items.length} water crafts for editing`);
    } catch (error) {
      console.error('❌ Error loading water crafts:', error);
      setWaterCrafts([]);
    }

    // Load Previous Claims
    try {
      const response = await axios.get(`${API_URL}/api/clients-personal-claims?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      const formattedClaims: PreviousClaim[] = items.map((claim: any) => ({
        id: claim.id,
        description: claim.description || '',
        policyNumber: claim.policy_number || claim.policyNumber || '',
        previousInsurer: claim.previous_insurer || claim.previousInsurer || '',
        claimDate: claim.claim_date || claim.claimDate || '',
        claimValue: claim.claim_value?.toString() || claim.claimValue?.toString() || '',
        isEditing: false
      }));
      setPreviousClaims(formattedClaims);
      console.log(`✅ Loaded ${formattedClaims.length} previous claims for editing`);
    } catch (error) {
      console.error('❌ Error loading previous claims:', error);
      setPreviousClaims([]);
    }

    // Load Previous Insurances
    try {
      const response = await axios.get(`${API_URL}/api/previous-insurances?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      const formattedInsurances: PreviousInsurance[] = items.map((ins: any) => ({
        id: ins.id,
        description: ins.description || '',
        policyNumber: ins.policy_number || ins.policyNumber || '',
        insurer: ins.insurer || '',
        startDate: ins.start_date || ins.startDate || '',
        endDate: ins.end_date || ins.endDate || '',
        isEditing: false
      }));
      setPreviousInsurances(formattedInsurances);
      console.log(`✅ Loaded ${formattedInsurances.length} previous insurances for editing`);
    } catch (error) {
      console.error('❌ Error loading previous insurances:', error);
      setPreviousInsurances([]);
    }

    // Load Notes
    try {
      const response = await axios.get(`${API_URL}/api/notes?quote_id=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data || response.data;
      const items = Array.isArray(data) ? data : [];
      const formattedNotes: Note[] = items.map((note: any) => ({
        id: note.id,
        content: note.note_content || note.content || '',
        createdAt: note.created_at || new Date().toISOString(),
        isEditing: false
      }));
      setNotes(formattedNotes);
      console.log(`✅ Loaded ${formattedNotes.length} notes for editing`);
    } catch (error) {
      console.error('❌ Error loading notes:', error);
      setNotes([]);
    }

    // Load Files
    try {
      const response = await axios.get(`${API_URL}/api/quotes/${quoteId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = response.data.data || response.data;
      
      // Handle different response structures for files
      let filesData: any[] = [];
      if (Array.isArray(data)) {
        filesData = data;
      } else if (data && data.documents && Array.isArray(data.documents)) {
        filesData = data.documents;
      } else if (data && data.data && Array.isArray(data.data)) {
        filesData = data.data;
      }
      
      const formattedFiles: UploadedFile[] = filesData.map((file: any) => ({
        id: file.id,
        file_name: file.file_name || file.filename || file.original_name || 'Document',
        file_size: file.file_size || file.size || 0,
        description: file.description || '',
        file_type: file.file_type || file.mime_type || file.mimetype || 'application/octet-stream',
        isEditing: false,
        url: file.url || file.file_path || file.path,
        data: file,
        alreadyUploaded: true,
        file_id: file.id
      }));
      setUploadedFiles(formattedFiles);
      console.log(`✅ Loaded ${formattedFiles.length} files for editing`);
    } catch (error) {
      console.error('❌ Error loading files:', error);
      setUploadedFiles([]);
    }

    console.log('📊 All products loaded for editing');
    
  } catch (error) {
    console.error('❌ Error loading products for editing:', error);
  }
}

  const handleClaimInputChange = (field: keyof PreviousClaim) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPreviousClaim(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    console.log(`📝 Claim ${field} changed to:`, event.target.value);
  };

const handleDriverLicensesChange = (licenses: DriverLicense[]) => {
      setPersonFormData(prev => ({
        ...prev,
        drivers_license_codes: licenses
      }));
    };

    // Function to update value-added products with quote ID
    const updateValueAddedProductsWithQuoteId = async (quoteId: number, clientId: string) => {
      try {
        console.log('🔄 Updating value-added products with quote ID:', quoteId, 'for client:', clientId);
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Get all value-added products for this client without quote_id
        const response = await axios.get(`${API_URL}/api/value-added?quote_id=${quoteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const products = response.data.data;
        console.log('📦 Found value-added products to update:', products);

        // Update each product that doesn't have a quote_id
        const updatePromises = products.map(async (product: any) => { // ← ADD ": any" HERE
          if (!product.quote_id) {
            console.log('🔄 Updating product:', product.id, 'with quote_id:', quoteId);
            
            const updateResponse = await axios.put(
              `${API_URL}/api/value-added/${product.id}`,
              { quote_id: quoteId },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            
            console.log('✅ Updated product:', product.id, 'response:', updateResponse.data);
            return updateResponse.data;
          }
          return null;
        });

        const results = await Promise.all(updatePromises);
        const updatedCount = results.filter(result => result !== null).length;
        
        console.log(`✅ Successfully updated ${updatedCount} value-added products with quote ID ${quoteId}`);
        return updatedCount;
        
      } catch (error) {
        console.error('❌ Error updating value-added products:', error);
        throw error;
      }
    };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Watermark Image */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      {/* Sidebar Component */}
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header Component */}
        <Header
          title="Create New Quote" // Specific title for this page
          user={user}
          client={client}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />

        {/* Page Content */}
        <div className="flex-1 p-6 overflow-auto">

          {/* Add the Back to Quotes Link HERE */}
          <div className="mb-4">
            <Link 
              to={`/personal/${clientId}`} 
              state={{ 
                activeTab: 'quotes',
                ...(isEditing && editingQuoteId ? { updatedQuoteId: editingQuoteId } : {})
              }}
              className="text-blue-500 hover:text-blue-700 font-semibold text-sm"
            >
              &larr; Back to {isEditing ? 'Quote Details' : 'Quotes'}
            </Link>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {error}
            </div>
          )}
          {saveStatus === 'success' && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
              {saveMessage}
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {saveMessage}
            </div>
          )}
          {loading && (
            <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative mb-4">
              Saving quote...
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[1600px] mx-auto">
            <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold">
                        {isEditing ? 'Edit Quote' : 'Create Quote'} for Client: {client?.name || 'Loading...'} (ID: {clientId})
                      </h2>
                      <p className="text-gray-700 mt-2">
                        {isEditing ? 'Edit the existing quote details below.' : 'This is where you will build your form to create a new quote.'}
                      </p>
                    </div>

                    <div className="w-1/3 ml-4">
                        <div className="mb-4">
                          <label htmlFor="quoteReference" className="block text-gray-700 text-sm font-bold mb-2">
                            Quote Reference:
                          </label>
                          <input
                            type="text"
                            id="quoteReference"
                            value={quoteReference}
                            onChange={handleQuoteReferenceChange}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            placeholder="e.g., Q-001-2024"
                          />
                        </div>
                      </div>
                    </div>
            
            <div className="space-y-4">
              {/* Declaration Questions Section */}
              <div className="space-y-2 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Declaration Questions</h3>
                  <div className="text-sm text-gray-600">
                    {isLoadingDeclarations ? 'Loading...' : `${declarationQuestions.length} questions available`}
                  </div>
                </div>
                
                {/* Add an info alert */}
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-sm text-blue-700">
                      Additional product-specific declaration questions will be asked during the quote process.
                    </span>
                  </div>
                </div>
                
                {isLoadingDeclarations ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <p className="text-gray-500 mt-2 text-sm">Loading declaration questions...</p>
                  </div>
                ) : declarationQuestions.length === 0 ? (
                  <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                    <p className="text-gray-500">No declaration questions configured.</p>
                    <Link 
                      to="/process-definitions/quotation-declarations" 
                      className="text-blue-600 hover:text-blue-800 text-sm mt-2 inline-block"
                    >
                      Configure declaration questions →
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                    {declarationQuestions.map((question) => (
                      <div key={question.id} className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            id={`declaration-${question.id}`}
                            checked={selectedDeclarations[question.id] || false}
                            onChange={(e) => handleDeclarationCheckboxChange(question.id, e.target.checked)}
                            className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </div>
                        <div className="ml-3 flex-1">
                          <label 
                            htmlFor={`declaration-${question.id}`} 
                            className="text-gray-700 cursor-pointer hover:text-gray-900 transition-colors"
                          >
                            {question.question}
                          </label>
                          {question.is_default && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Default
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
              </div>

              {/* Administrator and Underwriter Dropdowns Side by Side */}
              <div className="flex space-x-4 mb-6">
                {/* Administrator Dropdown with Tags */}
                <div className="flex-1">
                  <label htmlFor="administrators" className="block text-gray-700 text-sm font-bold mb-2">
                    Select Administrators
                  </label>
                  
                  {/* Selected Administrators Tags */}
                  {selectedAdministrators.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                    {selectedAdministrators.map(adminId => {
                    // ✅ Render the ID immediately if the name hasn't loaded yet
                    const admin = administrators.find(a => a.id === adminId);
                    return (
                      <div
                        key={adminId}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        <span>{admin ? admin.name : `Admin ID: ${adminId}`}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAdministrator(adminId)}
                          className="text-blue-600 hover:text-blue-800 font-bold text-lg leading-none"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}

                    </div>
                  )}

                  {/* Administrator Dropdown */}
                  <select
                    id="administrators"
                    name="administrators"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddAdministrator(e.target.value);
                        e.target.value = ''; // Reset the dropdown
                      }
                    }}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    disabled={isLoadingAdministrators}
                  >
                    <option value="">Select an administrator...</option>
                    {administrators.length === 0 && !isLoadingAdministrators ? (
                      <option value="" disabled>No administrators available</option>
                    ) : (
                      administrators
                        .filter(admin => !selectedAdministrators.includes(admin.id))
                        .map(admin => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name}
                          </option>
                        ))
                    )}
                  </select>
                  
                  {isLoadingAdministrators && (
                    <p className="text-xs text-gray-500 mt-1">Loading administrators...</p>
                  )}
                  {selectedAdministrators.length > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      {selectedAdministrators.length} administrator(s) selected
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Select administrators from the dropdown to add them
                  </p>
                </div>
                
                {/* Underwriter Dropdown with Tags */}
                <div className="flex-1">
                  <label htmlFor="underwriters" className="block text-gray-700 text-sm font-bold mb-2">
                    Select Underwriters
                  </label>
                  
                  {/* Selected Underwriters Tags */}
                  {selectedUnderwriters.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                  {selectedUnderwriters.map(underwriterId => {
                    const underwriter = underwriters.find(uw => uw.id.toString() === underwriterId);
                    return (
                      <div
                        key={underwriterId}
                        className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        <span>{underwriter ? underwriter.underwriter_name : `Underwriter ID: ${underwriterId}`}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveUnderwriter(underwriterId)}
                              className="text-purple-600 hover:text-purple-800 font-bold text-lg leading-none"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Underwriter Dropdown */}
                  <select
                    id="underwriters"
                    name="underwriters"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddUnderwriter(e.target.value);
                        e.target.value = ''; // Reset the dropdown
                      }
                    }}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value="">Select an underwriter...</option>
                    {underwriters
                      .filter(uw => !selectedUnderwriters.includes(uw.id.toString()))
                      .map(uw => (
                        <option key={uw.id} value={uw.id.toString()}>
                          {uw.underwriter_name}
                        </option>
                      ))
                    }
                  </select>
                  
                  {selectedUnderwriters.length > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      {selectedUnderwriters.length} underwriter(s) selected
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Select underwriters from the dropdown to add them
                  </p>
                </div>
              </div>

              {/* Start of new code for side-by-side dropdowns */}
              {!isEditing && (
                <div className="flex space-x-4 mb-6">
                  {/* Products Dropdown */}
                  <div className="flex-1">
                    <label htmlFor="product" className="block text-gray-700 text-sm font-bold mb-2">
                      Products
                    </label>
                    <select
                      id="product"
                      name="product"
                      value={selectedProduct}
                      onChange={handleProductChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="" disabled>Select a product</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Insurance Duration Dropdown */}
                  <div className="flex-1">
                    <label htmlFor="insuranceDuration" className="block text-gray-700 text-sm font-bold mb-2">
                      Previous Uninterrupted Insurance
                    </label>
                    <select
                      id="insuranceDuration"
                      name="insuranceDuration"
                      value={selectedDuration}
                      onChange={handleDurationChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="" disabled>Select duration</option>
                      {insuranceDurations.map(duration => (
                        <option key={duration.id} value={duration.id}>
                          {duration.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {!isEditing && selectedProduct === '3' && (
                <div className="mb-6">
                  <button
  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
  onClick={() => {
    // Log product addition - pass currentQuoteId (can be null)
    logProductAddition(currentQuoteId, 'vehicle', 'Vehicle');
    setEditingVehicleIndex(null);
    setEditingVehicleData(null);
    navigate(`/add-vehicle/${currentQuoteId}?clientId=${clientId}`);
  }}
>
  {vehicles.length > 0 ? 'Add Another Vehicle' : 'Add Vehicle'}
</button>
                </div>
              )}

              {/* Action buttons - Only show when NOT editing */}
              {!isEditing && (
                <div className="mb-6 flex space-x-4">
                {/* Previous Insurance button */}
                <button
  className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
  onClick={() => {
    console.log('🔍 Navigating with quoteId:', currentQuoteId);
    
    // ✅ SAVE ADMINISTRATORS BEFORE NAVIGATING
    localStorage.setItem('temp_selected_administrators', JSON.stringify(selectedAdministrators));
    localStorage.setItem('temp_selected_underwriters', JSON.stringify(selectedUnderwriters));
    
    // Log product addition - pass currentQuoteId (can be null)
    logProductAddition(currentQuoteId, 'previous_insurance', 'Previous Insurance');
    navigate(`/add-previous-insurance/${currentQuoteId}?clientId=${clientId}`);
  }}
>
  Previous Insurance
</button>

                  {/* claims button */}
                  <button
  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
  onClick={() => {
    // Log product addition - pass currentQuoteId (can be null)
    logProductAddition(currentQuoteId, 'claim', 'Previous Claim');
    navigate(`/add-previous-claim/${currentQuoteId}?clientId=${clientId}`);
  }}
>
  Create Previous Claim
</button>
                  
                  {/* create notes button */}
                  <button
  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
  onClick={() => {
    // Log product addition - pass currentQuoteId (can be null)
    logProductAddition(currentQuoteId, 'note', 'Note');
    navigate(`/add-note/${currentQuoteId}?clientId=${clientId}`);
  }}
>
  Create Note
</button>

                  {/* create file button */}
                  <button
  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
  onClick={() => {
    // Log product addition - pass currentQuoteId (can be null)
    logProductAddition(currentQuoteId, 'file', 'File');
    // Always allow navigation, pass currentQuoteId (may be null for new quotes)
    navigate('/add-file', { 
      state: { 
        quoteId: currentQuoteId, 
        clientId: clientId 
      } 
    });
  }}
>
  Create File
</button>

                </div>
              )}

              {/* Product Cards Section - Show for both new and edit mode */}
<div className="mb-6">
  <h3 className="text-lg font-semibold mb-4">Added Products</h3>
  
  {/* Loading State */}
  {isLoadingProducts ? (
    <div className="text-center py-8">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      <p className="text-gray-500 mt-2">Loading products...</p>
    </div>
  ) : (
    /* Products Grid */
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      
      {/* Property Cards */}
      {properties.map((property, index) => (
        <EditablePropertyCard
          key={`property-${property.id || index}`}
          property={property}
          index={index}
          onSave={(index, updatedData) => handleSaveEdit('property', index, updatedData)}
          onCancel={(index) => handleCancelEdit('property', index)}
          onEdit={(index) => handleEditItem('property', index)}
          cardNumber={index + 1}
        />
      ))}

      {/* Household Contents Cards */}
      {householdContents.map((content, index) => (
        <EditableHouseholdContentsCard
          key={`household-${content.id || index}`}
          contents={content}
          index={index}
          onSave={(index, updatedData) => handleSaveEdit('household', index, updatedData)}
          onCancel={(index) => handleCancelEdit('household', index)}
          onEdit={(index) => handleEditItem('household', index)}
          cardNumber={index + 1}
        />
      ))}

      {/* Vehicle Cards */}
      {vehicles.map((vehicle, index) => (
        <EditableVehicleCard
          key={`vehicle-${vehicle.id || index}`}
          vehicle={vehicle}
          index={index}
          onEdit={() => handleEditItem('vehicle', index)}
          cardNumber={index + 1}
        />
      ))}

      {/* Trailer & Caravan Cards */}
      {trailersCaravans.map((trailer, index) => (
        <EditableTrailerCaravanCard
          key={`trailer-${trailer.id || index}`}
          trailerCaravan={trailer}
          index={index}
          onSave={(index, updatedData) => handleSaveEdit('trailer', index, updatedData)}
          onCancel={(index) => handleCancelEdit('trailer', index)}
          onEdit={(index) => handleEditItem('trailer', index)}
          cardNumber={index + 1}
        />
      ))}

      {/* Value Added Products Cards */}
      {valueAddedProducts.map((product, index) => {
        console.log(`🔄 DEBUG: Product at index ${index}:`, {
          id: product.id,
          tempId: product.tempId,
          index: index,
          productType: product.productType,
          description: product.description
        });

        const stableKey = `value-added-${
          product.id || product.tempId || `index-${index}`
        }-${product.productType}-${
          product.description?.substring(0, 20) || 'no-desc'
        }-${index}`;
        
        return (
          <EditableValueAddedCard
            key={stableKey}
            valueAddedProduct={product}
            index={index}
            onSave={(index, updatedData) => handleSaveEdit('valueAdded', index, updatedData)}
            onCancel={(index) => handleCancelEdit('valueAdded', index)}
            onEdit={(index) => handleEditItem('valueAdded', index)}
            cardNumber={index + 1}
          />
        );
      })}

      {/* Risk Items Cards */}
      {riskItems.map((riskItem, index) => (
        <EditableRiskItemCard
          key={`risk-item-${riskItem.id || index}`}
          riskItem={riskItem}
          index={index}
          onSave={(index, updatedData) => handleSaveEdit('riskItem', index, updatedData)}
          onCancel={(index) => handleCancelEdit('riskItem', index)}
          onEdit={(index) => handleEditItem('riskItem', index)}
          cardNumber={index + 1}
        />
      ))}

      {/* Water Craft Cards */}
      {waterCrafts.map((waterCraft, index) => (
        <EditableWaterCraftCard
          key={`water-craft-${waterCraft.id || index}`}
          waterCraft={waterCraft}
          index={index}
          onSave={(index, updatedData) => handleSaveEdit('waterCraft', index, updatedData)}
          onCancel={(index) => handleCancelEdit('waterCraft', index)}
          onEdit={(index) => handleEditItem('waterCraft', index)}
          cardNumber={index + 1}
        />
      ))}

      {/* Previous Insurance Cards */}
      {previousInsurances.map((insurance, index) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };
  
  const insurancePeriod = `${formatDate(insurance.startDate)} - ${formatDate(insurance.endDate)}`;
  
  // Create a unique key that combines id, index, and a timestamp
  const uniqueKey = insurance.id || `insurance-${index}-${Date.now()}`;
  
  return (
    <div key={uniqueKey} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-green-600">Previous Insurance</h4>
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Insurance</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex flex-col">
                  <span className="text-gray-600 mb-1">Description:</span>
                  <span className="font-medium text-gray-800 text-xs bg-gray-50 p-2 rounded border">
                    {insurance.description}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Policy Number:</span>
                  <span className="font-medium">{insurance.policyNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Insurer:</span>
                  <span className="font-medium">{insurance.insurer}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Insurance Period:</span>
                  <span className="font-medium">{insurancePeriod}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Previous Claims Cards */}
      {previousClaims.map((claim, index) => {
        const claimValue = claim.claimValue ? `R ${parseFloat(claim.claimValue).toLocaleString()}` : 'Not specified';
        const formattedDate = claim.claimDate ? new Date(claim.claimDate).toLocaleDateString() : 'Not specified';
        
        return (
          <div key={`claim-${index}`} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-indigo-600">Previous Claim</h4>
                <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">Claim</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex flex-col">
                  <span className="text-gray-600 mb-1">Description:</span>
                  <span className="font-medium text-gray-800 text-xs bg-gray-50 p-2 rounded border">
                    {claim.description}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Policy Number:</span>
                  <span className="font-medium">{claim.policyNumber}</span>
                </div>
                {claim.previousInsurer && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Previous Insurer:</span>
                    <span className="font-medium">{claim.previousInsurer}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Claim Date:</span>
                  <span className="font-medium">{formattedDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Claim Value:</span>
                  <span className="font-medium text-red-600">{claimValue}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Notes Cards */}
      {notes.map((note, index) => (
        <EditableNoteCard
          key={note.id ? `note-${note.id}` : `note-temp-${index}-${Date.now()}`}
          note={note}
          index={index}
          onSave={(index, updatedData) => handleSaveEdit('note', index, updatedData)}
          onCancel={(index) => handleCancelEdit('note', index)}
          onEdit={(index) => handleEditItem('note', index)}
        />
      ))}

            {/* Files Cards */}
      {uploadedFiles.map((file, index) => (
        <div key={file.id ? `file-${file.id}` : `file-temp-${index}-${Date.now()}`}>
          <EditableFileCard
            file={{
              ...file,
              file_name: file.file_name || 'Untitled Document',
              file_size: file.file_size || 0,
              description: file.description || file.file_name || 'Uploaded Document',
              // Ensure document type is passed for display
              document_type: file.document_type || 'quote' 
            }}
            index={index}
            onSave={(index, updatedData) => handleSaveEdit('file', index, updatedData)}
            onCancel={(index) => handleCancelEdit('file', index)}
            onEdit={(index) => handleEditItem('file', index)}
          />
        </div>
      ))}
    </div>
  )}

  {/* Empty State - only show when not loading and no items */}
  {!isLoadingProducts && properties.length === 0 && 
    householdContents.length === 0 && 
    vehicles.length === 0 && 
    trailersCaravans.length === 0 && 
    valueAddedProducts.length === 0 && 
    riskItems.length === 0 &&
    waterCrafts.length === 0 &&
    previousClaims.length === 0 && 
    previousInsurances.length === 0 &&
    notes.length === 0 && 
    uploadedFiles.length === 0 && (
    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
      <div className="text-gray-400 mb-2">
        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <p className="text-gray-500">No items added yet. Select a product from the dropdown above to get started.</p>
    </div>
  )}
</div>

              {/* Assign to Different Consultant Checkbox - Only show when NOT editing */}
              {!isEditing && (
                <div className="pt-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="assignConsultant"
                      checked={assignToDifferentConsultant}
                      onChange={handleAssignConsultantChange}
                      className="form-checkbox h-5 w-5 text-blue-600 rounded"
                    />
                    <label htmlFor="assignConsultant" className="ml-2 text-gray-700 font-bold">
                      Assign this quote to a different consultant
                    </label>
                  </div>

                  {/* Consultant Dropdown (only shown when checkbox is checked) */}
                  {assignToDifferentConsultant && (
                    <div className="mt-3 ml-7 w-full max-w-md">
                      <label htmlFor="consultantSelect" className="block text-gray-700 font-bold mb-2 text-sm">
                        Select Consultant
                      </label>
                      <select
                        id="consultantSelect"
                        value={selectedConsultant}
                        onChange={handleConsultantChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        disabled={isLoadingConsultants}
                      >
                        <option value="">Select a consultant</option>
                        {isLoadingConsultants ? (
                          <option value="" disabled>Loading consultants...</option>
                        ) : consultants.length === 0 ? (
                          <option value="" disabled>No consultants available</option>
                        ) : (
                          consultants.map(consultant => (
                            <option key={consultant.id} value={consultant.id}>
                              {consultant.name}
                            </option>
                          ))
                        )}
                      </select>
                      {isLoadingConsultants && (
                        <p className="text-xs text-gray-500 mt-1">Loading consultants...</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    // Clear sessionStorage drafts
                    sessionStorage.removeItem(`draft_quote_ref_${clientId}`);
                    sessionStorage.removeItem(`draft_quote_duration_${clientId}`);
                    sessionStorage.removeItem(`draft_quote_admin_${clientId}`);
                    sessionStorage.removeItem(`draft_quote_underwriter_${clientId}`);
                    sessionStorage.removeItem(`draft_quote_declarations_${clientId}`);

                    // Clear ALL localStorage when user explicitly cancels
                    const draftKeys = [
                    `client_${clientId}_products`,
                    `client_${clientId}_previous_insurances_draft`,
                    `client_${clientId}_previous_claims_draft`,
                    `client_${clientId}_notes_draft`,
                    `client_${clientId}_properties_draft`,
                    `client_${clientId}_household_contents_draft`,
                    `client_${clientId}_vehicles_draft`,
                    `client_${clientId}_trailers_draft`,
                    `client_${clientId}_value_added_draft`,
                    `client_${clientId}_risk_items_draft`,
                    `client_${clientId}_water_crafts_draft`,
                    `client_${clientId}_files_draft`
                  ];
    
    draftKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🗑️ Cleared: ${key}`);
      }
    });
    console.log('🧹 User cancelled, cleared all localStorage');
    
    // Clear state
    setProperties([]);
    setVehicles([]);
    setHouseholdContents([]);
    setTrailersCaravans([]);
    setValueAddedProducts([]);
    setRiskItems([]);
    setPreviousClaims([]);
    setPreviousInsurances([]);
    setNotes([]);
    setUploadedFiles([]);
    
    // Navigate back
    navigate(`/personal/${clientId}`, { state: { activeTab: 'quotes' } });
  }}
  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
>
  Cancel
</button>
                
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                >
                  {isEditing ? 'Update Quote' : 'Save Quote'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Modal */}
      {isVehicleModalVisible && (
        <AddVehicleModal
          isVisible={isVehicleModalVisible}
          onClose={() => {
            setIsVehicleModalVisible(false);
            setEditingVehicleIndex(null);
            setEditingVehicleData(null);
          }}
          onSave={handleSaveVehicle}
          openPersonModal={() => setIsPersonModalVisible(true)}
          clientId={parseInt(clientId!)}
          vehicle={editingVehicleData || undefined}
          mode={editingVehicleIndex !== null ? 'edit' : 'create'}
          setSaveStatus={setSaveStatus}
          setSaveMessage={setSaveMessage}
          personsOfInterest={personsOfInterest.filter(person => person.status !== 'inactive')}
        />
      )}

      {/* ADD PERSON MODAL */}
      {isPersonModalVisible && (
        <AddPersonModal
          isOpen={isPersonModalVisible}
          person={personFormData}
          errors={personErrors}
          editingPersonId={null}
          onClose={handleClosePersonModal}
          onSubmit={handlePersonSubmit}
          onInputChange={handlePersonInputChange}
          onDriverLicensesChange={handleDriverLicensesChange}
        />
      )}

      {/* household contents modal */}
      {isHouseholdContentsModalVisible && (
        <AddHouseholdContentsModal
          isVisible={isHouseholdContentsModalVisible}
          onClose={handleCloseHouseholdContentsModal}
          onSave={handleSaveHouseholdContents}
          clientId={parseInt(clientId)}
          quoteId={-1}
        />
      )}

      {/* Trailer and Caravan Modal */}
      {isTrailerCaravanModalVisible && (
  <TrailerCaravanModal
    isVisible={isTrailerCaravanModalVisible}
    onClose={() => {
      setIsTrailerCaravanModalVisible(false);
    }}
    onSave={(trailerCaravanData) => {
      console.log('🟢 DEBUG CreateQuotePage: TrailerCaravanModal onSave called with:', {
        data: trailerCaravanData,
        keys: Object.keys(trailerCaravanData),
        clientId
      });
      
      const newTrailer = {
        ...trailerCaravanData,
        id: `temp-trailer-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
        isEditing: false
      };
      
      console.log('📦 DEBUG: Adding trailer to state:', {
        newTrailer,
        currentTrailersCount: trailersCaravans.length,
        allTrailerIds: trailersCaravans.map(t => t.id)
      });
      
      // Check for duplicates
      const isDuplicate = trailersCaravans.some(t => 
        t.registrationNumber === newTrailer.registrationNumber ||
        (t.id && t.id === newTrailer.id)
      );
      
      if (isDuplicate) {
        console.warn('⚠️ DEBUG: Duplicate trailer detected, not adding');
        setSaveStatus('warning');
        setSaveMessage('This trailer appears to already be added!');
        return;
      }
      
      setTrailersCaravans(prev => {
        const newState = [...prev, newTrailer];
        console.log('✅ DEBUG: State updated, new count:', newState.length);
        console.log('📊 DEBUG: All trailers after add:', newState);
        return newState;
      });
      
      setSaveStatus('success');
      setSaveMessage('Trailer/Caravan added — will save when quote is created');
      setIsTrailerCaravanModalVisible(false);
    }}
    clientId={parseInt(clientId)}
    setSaveStatus={setSaveStatus}
    setSaveMessage={setSaveMessage}
    
  />
)}

      {/* Value Added Modal */}
      {isValueAddedModalVisible && (
        <QuoteValueAddedModal
          isVisible={isValueAddedModalVisible}
          onClose={handleCloseValueAddedModal}
          onSave={handleSaveValueAdded}
          clientId={parseInt(clientId)}
          setSaveStatus={setSaveStatus}
          setSaveMessage={setSaveMessage}
          mode="create"
        />
      )}
      
      {/* Risk Items Modal */}
      {isRiskItemsModalVisible && (
        <QuoteRiskItemsModal
          isVisible={isRiskItemsModalVisible}
          onClose={handleCloseRiskItemsModal}
          onSave={handleSaveRiskItems}
          clientId={parseInt(clientId)}
          setSaveStatus={setSaveStatus}
          setSaveMessage={setSaveMessage}
        />
      )}

      {/* Water Craft Modal */}
        {isWaterCraftModalVisible && (
          <WaterCraftModal
            isVisible={isWaterCraftModalVisible}
            onClose={() => setIsWaterCraftModalVisible(false)}
            onSave={(waterCraftData) => {
              const newWaterCraft = {
                ...waterCraftData,
                id: `temp-watercraft-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
              };
              setWaterCrafts(prev => [...prev, newWaterCraft]);
              setSaveStatus('success');
              setSaveMessage('Water craft added — saved when quote is created');
              setIsWaterCraftModalVisible(false);
            }}
            clientId={parseInt(clientId)}
          />
        )}

    </div>
  );
};



export default CreateQuotePage;
